/**
 * @file MainPage.js
 * @description Auto-maintained cleanup: comments & minor a11y/robustness. No UI/feature changes.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';

import NarrativeWorkbench from './NarrativeWorkbench';
import ArtifactWorkbench from './ArtifactWorkbench';
import * as artifactStore from '../services/ArtifactStore';
import * as metering from '../services/metering';

import {
  addProject, getProjects, saveScreenplay, getScreenplay, saveScreenplayOutline, getScreenplayOutline, saveScenes, getScenes, saveVideoOnCloud, deleteProject,
  saveAudioOnCloud, saveCharacterBible, getCharacterBible, saveImageOnCloud, saveImageOnCloudB64, updateWorkflowState, mergeScenes, getModels, saveMergedVideoUrl
} from '../services/db';

import {
  summarizeStrictCharacter, downloadToBlob, blobToBase64, generateCharacterDescriptions, generateCharacterDescriptionsConcise, buildDetailedVideoPrompt, buildScreenplayLite,
  normalizeCharacterBible, normalizeScenesFromJson
} from '../utilities/utility';

// Tutorial-aware calls — routed through interceptor (mock or real)
import {
  createVideoJob, getVideoJob,
  generateSceneShotVideos, submitTranscoderMerge, getTranscoderJob,
  ImageAPI,
  generateScreenplayOutline, generateCharacterBibleStrict, enrichCharacters, getScreenplayDetailJob
} from '../services/TutorialInterceptor';

// Non-interceptable calls — always hit real backend
import { regenerateCharacterFull, validateContent } from '../services/backend/textService.backend';
import { generateSceneShotImages, generateSceneShotImageEdits } from '../services/backend/imageService.backend';
import { generateVideoFromPrompt, submitTranscoderSmooth, generateShotVideo, DEFAULT_CINEMATIC_CONTEXT } from '../services/backend/videoService.backend';
import { generateMusicFromPrompt } from '../services/backend/musicService.backend';
import { getSpeechFromText } from '../services/backend/ttsService.backend';
import { getVideoFrameThumb } from './videoThumb';

import { checkCloudConnectivity, checkLocalConnectivity } from '../services/healthService';
import logger from '../services/logger';

import ProjectSidebar from './ProjectSidebar';
import NewProjectDialog from './NewProjectDialog';
import { useNavigate } from 'react-router-dom';
import { defaultModelOptions } from './constants/models';

import './MainPage.css';
import { useOnboarding } from './OnboardingContext';
import TourTooltip from './TourTooltip';
import { fetchWithTrace } from '../services/fetchWithTrace';

// === WORKFLOW STATE PATCH: add after imports ===

const defaultModels = {
  screenplay: 'gpt-5.2',
  characterBible: 'gpt-image-1',
  scenes: 'gemini-3-pro-image-preview',
  videos: 'veo',
};

const defaultWorkflow = {
  screenplay: { complete: false, model: defaultModels.screenplay },
  characterBible: { complete: false, model: defaultModels.characterBible },
  scenes: { complete: false, model: defaultModels.scenes },
  videos: { complete: false, model: defaultModels.videos },
};
// === END PATCH ===

const QUICK_IMAGE_NAME = 'Quick: Images';
const QUICK_VIDEO_NAME = 'Quick: Videos';
const QUICK_AUDIO_NAME = 'Quick: Audio';
const LS_QUICK_IDS = (uid) => `quick_project_ids_v1:${uid || 'guest'}`;

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';



const MainPage = () => {
  const { startTour, isActive } = useOnboarding();
  const [projects, setProjects] = useState([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [showNewProjectDialog, setShowNewProjectDialog] = useState(false);
  const [scenes, setScenes] = useState([]);
  const [detailedScreenplay, setDetailedScreenplay] = useState('');
  const [loading, setLoading] = useState(false);
  const [characterBible, setCharacterBible] = useState({});
  const [isScreenplayRendered, setIsScreenplayRendered] = useState(false);
  const [isScenesGenerating, setIsScenesGenerating] = useState(false);
  const [isScreenplayLoading, setIsScreenplayLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isCharacterSlideshow, setIsCharacterSlideshow] = useState(false);
  const [isCharacterBibleLoading, setIsCharacterBibleLoading] = useState(false);
  const [maximizeScreenPlay, setMaximizeScreenPlay] = useState(true);
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);

  const [videoIsLoading, setVideoIsLoading] = useState({});
  const [workflowState, setWorkflowState] = useState(() => {
    const stored = localStorage.getItem('workflowState');
    return stored ? JSON.parse(stored) : {};
  });
  const workflowStateRef = useRef(workflowState);

  useEffect(() => {
    workflowStateRef.current = workflowState;
  }, [workflowState]);

  // Bug Fix: Track mounted state to prevent unsafe async updates
  const isMounted = useRef(true);
  useEffect(() => {
    return () => { isMounted.current = false; };
  }, []);

  // Auto-Start Tour for First-Time Users
  // startTour() now immediately marks the tour as complete in localStorage + backend,
  // so a refresh, logout, or tab-close mid-tour will never re-show the tour.
  useEffect(() => {
    if (projectsLoaded) {
      if (localStorage.getItem('has_completed_tutorial') === 'false' && !isActive) {
        startTour();
      }
    }
  }, [projectsLoaded, startTour, isActive]);

  const [videoDots, setVideoDots] = useState({});
  const [isBatchVideoGenRunning, setIsBatchVideoGenRunning] = useState(false);
  const [mergedVideoUrl, setMergedVideoUrl] = useState('');
  const [isSceneMerging, setIsSceneMerging] = useState({}); // PARITY: Track merge state per scene (Shot -> Scene)
  const [isMerging, setIsMerging] = useState(false); // RESTORE: Track global merge (Scenes -> Movie)
  const [showScenes, setShowScenes] = useState(false);
  const navigate = useNavigate();
  const [selectedArtifactType, setSelectedArtifactType] = useState(null); // New state for artifact type
  const [artifactDialogOpen, setArtifactDialogOpen] = useState(false);
  const [artifactMode, setArtifactMode] = useState('image'); // 'image'|'video'|'audio'
  const [characterIsRegenerating, setCharacterIsRegenerating] = useState({}); // NEW
  const [artifacts, setArtifacts] = useState(null);
  const [artifactWorkbenchMode, setArtifactWorkbenchMode] = useState(null);
  const [modelOptions, setModelOptions] = useState(defaultModelOptions);

  const [quickProjects, setQuickProjects] = useState({ image: null, video: null, audio: null });
  const [quickImages, setQuickImages] = useState(null); // null means loading
  const [quickVideos, setQuickVideos] = useState(null);
  const [quickAudio, setQuickAudio] = useState(null);

  // OPTIONAL: options we pass into the workbench when used as playground
  const [workbenchOptions, setWorkbenchOptions] = useState(null);
  const [artifactPrefill, setArtifactPrefill] = useState(null);

  const [screenplayOutline, setScreenplayOutline] = useState(null);     // outline JSON
  const [isDetailsGenerating, setIsDetailsGenerating] = useState(false); // header badge

  const [detailsJobId, setDetailsJobId] = useState(null);
  const [hydratedByNumber, setHydratedByNumber] = useState({});

  const [isCharacterEnriching, setIsCharacterEnriching] = useState(false);

  const [userStatus, setUserStatus] = useState(() => {
    return localStorage.getItem('userStatus') || 'active';
  });

  /* --- Global Error Toast --- */
  const [toast, setToast] = useState(null);
  useEffect(() => {
    const handleAlephError = (e) => {
      const { status, message } = e.detail || {};
      setToast({ status, message });
      setTimeout(() => setToast(null), 5000);
    };
    window.addEventListener('aleph-error', handleAlephError);
    return () => window.removeEventListener('aleph-error', handleAlephError);
  }, []);

  const autoCBTriggeredRef = useRef(false);
  const isWaitlisted = userStatus === 'waitlisted';


  useEffect(() => {
    // Create a timer for each generating scene
    const timers = {};
    Object.entries(videoIsLoading).forEach(([index, isLoading]) => {
      if (isLoading) {
        let i = 1;
        timers[index] = setInterval(() => {
          setVideoDots(prev => ({
            ...prev,
            [index]: '.'.repeat(i)
          }));
          i = i < 3 ? i + 1 : 1;
        }, 500);
      }
    });
    return () => Object.values(timers).forEach(clearInterval);
  }, [videoIsLoading]);

  useEffect(() => {
    if (mergedVideoUrl) {
      //setShowScenes(false);
      setShowScenes(true);
    }
  }, [mergedVideoUrl]);

  useEffect(() => {
    localStorage.setItem('workflowState', JSON.stringify(workflowState));
  }, [workflowState]);


  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!currentProject?._id) return;
      await new Promise(res => setTimeout(res, 30000));
      try {
        const outlineFromDb = await getScreenplayOutline(currentProject._id);  // backend fetch
        if (!cancelled && outlineFromDb) {
          setScreenplayOutline(outlineFromDb);
          setIsScreenplayRendered(true);
        }
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.warn('Outline fetch failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [currentProject?._id]);

  useEffect(() => {
    // Separate checks; harmless no-ops in production other than logging
    checkCloudConnectivity();

    if (process.env.NODE_ENV !== 'production') {
      checkLocalConnectivity();
    }
    logger.info('MainPage initialized', { apiUrl: process.env.REACT_APP_API_URL });
    loadProjects();
    // Fetch dynamic models
    getModels().then(data => {
      if (data) {
        setModelOptions(prev => ({ ...prev, ...data }));
      }
    });
  }, []);

  const currentUserId = localStorage.getItem('userId'); // already present

  useEffect(() => {

    if (!projectsLoaded) return;

    let alive = true;
    (async () => {
      try {
        // 1) read per-user cache
        const key = LS_QUICK_IDS(currentUserId);
        let cached = null;
        try {
          cached = JSON.parse(localStorage.getItem(key) || 'null');
        } catch { }
        let imageId = cached?.image || null;
        let videoId = cached?.video || null;

        // 2) validate cached IDs belong to THIS user's project list
        const idsSet = new Set((projects || []).map(p => p._id || p.id));
        if (imageId && !idsSet.has(imageId)) imageId = null;
        if (videoId && !idsSet.has(videoId)) videoId = null;

        // 3) try to resolve by title in current list
        if (!imageId) {
          const f = (projects || []).find(p => p?.title === QUICK_IMAGE_NAME);
          if (f) imageId = f._id || f.id;
        }
        if (!videoId) {
          const f = (projects || []).find(p => p?.title === QUICK_VIDEO_NAME);
          if (f) videoId = f._id || f.id;
        }
        let audioId = cached?.audio || null;
        if (audioId && !idsSet.has(audioId)) audioId = null;
        if (!audioId) {
          const f = (projects || []).find(p => p?.title === QUICK_AUDIO_NAME);
          if (f) audioId = f._id || f.id;
        }

        // 4) create if missing
        if (!imageId) imageId = await ensureProject(QUICK_IMAGE_NAME);
        if (!videoId) videoId = await ensureProject(QUICK_VIDEO_NAME);
        if (!audioId) audioId = await ensureProject(QUICK_AUDIO_NAME);

        // 5) commit and cache
        const ids = { image: imageId, video: videoId, audio: audioId };
        if (!alive) return;
        setQuickProjects(ids);
        localStorage.setItem(key, JSON.stringify(ids));

        // 6) fetch recent thumbnails
        const [imgs, vids, auds] = await Promise.all([
          artifactStore.listRecent(imageId, 'image', 1000),
          artifactStore.listRecent(videoId, 'video', 1000),
          artifactStore.listRecent(audioId, 'audio', 1000),
        ]);
        if (!alive) return;
        setQuickImages(imgs || []);
        setQuickVideos(vids || []);
        setQuickAudio(auds || []);

        (async () => {
          const enriched = await Promise.all((vids || []).map(async a => {
            if (a.thumbnailUrl) return a;
            const u = a.outputUrl || a.videoUrl || a.url;
            if (!u) return a;
            try {
              const t = await getVideoFrameThumb(u, 1.0, 120);
              return t ? { ...a, thumbnailUrl: t } : a;
            } catch { return a; }
          }));
          if (alive) setQuickVideos(enriched);
        })();


      } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.warn('Quick project bootstrap failed:', e);
      }
    })();
    return () => { alive = false; };
  }, [currentUserId, projects, projectsLoaded]);


  async function ensureProject(title, description = 'Quick playground bucket') {
    // 0) in-memory check
    const mem = (projects || []).find(p => p?.title === title);
    if (mem) return mem._id || mem.id;

    // 1) server-side probe by title (prevents dupes if local state is stale)
    try {
      const list = await getProjects();
      const byTitle = (Array.isArray(list) ? list : []).find(p => p?.title === title);
      if (byTitle) return byTitle._id || byTitle.id;
    } catch { /* best-effort probe */ }

    // 2) create (using secured service)
    try {
      // addProject expects { title, story } and handles the API call
      const project_id = await addProject({ title, story: description });
      return project_id;
    } catch (e) {
      throw new Error(`Create project failed: ${e.message}`);
    }
  }

  const openArtifactGenerator = useCallback((mode) => {
    setArtifactMode(mode);
    setArtifactDialogOpen(true);
  }, []);

  const onOpenAudioPlayground = useCallback(() => {
    setArtifactWorkbenchMode('audio');
    setSelectedArtifactType('audio');
    setWorkbenchOptions({
      quickMode: true,
      defaultProjectId: quickProjects.audio,
    });
    setArtifactPrefill(null); // start fresh
  }, [quickProjects.audio]);


  // Quick IDs helper
  const quickId = (t) => (quickProjects?.[t]?._id || quickProjects?.[t] || null);
  const isQuickPid = (pid) => {
    const qids = [quickId('image'), quickId('video'), quickId('audio')].filter(Boolean);
    return qids.includes(pid);
  };

  // Unified list: if viewing a quick project, merge all quick buckets
  const listArtifactsSmart = async (pid) => {
    if (isQuickPid(pid)) {
      const qi = quickId('image');
      const qv = quickId('video');
      const qa = quickId('audio');

      const [imgRes, vidRes, audRes] = await Promise.all([
        qi ? artifactStore.listArtifacts(qi).catch(() => ({ image: [] })) : { image: [] },
        qv ? artifactStore.listArtifacts(qv).catch(() => ({ video: [] })) : { video: [] },
        qa ? artifactStore.listArtifacts(qa).catch(() => ({ audio: [] })) : { audio: [] },
      ]);

      return {
        image: imgRes.image || [],
        video: vidRes.video || [],
        audio: audRes.audio || [],
      };
    }
    // Normal project
    return artifactStore.listArtifacts(pid);
  };

  // When deleting in unified-quick view, route to the bucket that matches the kind
  const pidForKind = (displayPid, kind) => (isQuickPid(displayPid) ? quickId(kind) : displayPid);

  const enrichVideoThumbs = async (list) => {
    return Promise.all((list || []).map(async a => {
      if (a.thumbnailUrl) return a;
      const videoUrl = a.outputUrl || a.videoUrl || a.url;
      try {
        const thumb = await getVideoFrameThumb(videoUrl, 1.0);
        return thumb ? { ...a, thumbnailUrl: thumb } : a;
      } catch {
        return a; // leave as-is if frame-grab fails
      }
    }));
  };

  const handleWorkbenchSave = async (data) => {
    // Pick the right project to save into
    const isQuickMode = !!workbenchOptions?.quickMode;
    const pid =
      (!isQuickMode && currentProject?._id) ||
      (data.type === 'image'
        ? (quickProjects.image?._id || quickProjects.image)
        : data.type === 'video'
          ? (quickProjects.video?._id || quickProjects.video)
          : (quickProjects.audio?._id || quickProjects.audio));

    if (!pid) {
      if (process.env.NODE_ENV !== 'production') console.warn('No project id available for save.');
      return;
    }

    // Ensure videos have a thumb if the workbench didn't supply one
    let toSave = data;
    if (data.type === 'video' && !data.thumbnailUrl) {
      const videoUrl = data.outputUrl || data.videoUrl;
      try {
        const thumb = await getVideoFrameThumb(videoUrl, 1.0);
        if (thumb) toSave = { ...data, thumbnailUrl: thumb };
      } catch { }
    }

    const saved = await artifactStore.saveArtifact(pid, toSave);

    // Which project is the library *currently* showing? (selected project or quick project)
    const displayPid = isQuickMode
      ? (workbenchOptions?.defaultProjectId || pid)
      : (currentProject?._id || workbenchOptions?.defaultProjectId || null);

    // Optimistically insert into the visible library (covers quick mode + normal projects)
    if (displayPid === pid) {
      setArtifacts(prev => {
        const base = prev || { image: [], video: [], audio: [] };
        return { ...base, [saved.type]: [saved, ...(base[saved.type] || [])] };
      });
    }


    if (displayPid) {
      listArtifactsSmart(displayPid).then(setArtifacts).catch(() => { });
    }

    // Optimistically update playground recents if saving into a quick bucket
    if (getQuickId(saved.type) === pid) {
      if (saved.type === 'image') setQuickImages(v => [saved, ...(v || [])]);
      if (saved.type === 'video') setQuickVideos(v => [saved, ...(v || [])]);
      if (saved.type === 'audio') setQuickAudio(v => [saved, ...(v || [])]);
    }

    // Re-sync after a short delay (helps with any server/cache lag)
    setTimeout(async () => {
      if (getQuickId('image') === pid) {
        setQuickImages(await artifactStore.listRecent(pid, 'image', 20));
      }
      if (getQuickId('video') === pid) {
        setQuickVideos(await artifactStore.listRecent(pid, 'video', 20));
      }
      if (displayPid === pid) {
        const grouped = await listArtifactsSmart(pid);
        setArtifacts(grouped);
      }
    }, 300);
  };

  const handleDeleteArtifact = async (artifact, kind, projectIdOverride = null) => {
    // Which library are we showing?
    const isQuickMode = !!workbenchOptions?.quickMode;
    let displayPid = projectIdOverride || (isQuickMode ? workbenchOptions?.defaultProjectId : currentProject?._id) || workbenchOptions?.defaultProjectId || null;

    // FIX: Detect if this is a Quick Project artifact when coming from sidebar (projectIdOverride=null)
    const qId = quickId(kind);
    if (!projectIdOverride && qId) {
      if (artifact?.projectId) {
        displayPid = artifact.projectId;
      } else {
        // Fallback check
        const isQuick = (kind === 'image' && quickImages?.some(a => (a._id || a.id) === (artifact._id || artifact.id))) ||
          (kind === 'video' && quickVideos?.some(a => (a._id || a.id) === (artifact._id || artifact.id))) ||
          (kind === 'audio' && quickAudio?.some(a => (a._id || a.id) === (artifact._id || artifact.id)));
        if (isQuick) displayPid = qId;
      }
    }

    if (!displayPid) return;

    // Route DELETE to the correct project id
    const pid = pidForKind(displayPid, kind);  // <— key change

    const id = artifact?._id || artifact?.id;
    if (!id) return;

    // optimistic remove
    setArtifacts(prev => {
      const base = prev || { image: [], video: [], audio: [] };
      return { ...base, [kind]: (base[kind] || []).filter(a => (a._id || a.id) !== id) };
    });

    try {
      await artifactStore.deleteArtifact(pid, id);
    } finally {
      // authoritative refresh with unified quick support
      const grouped = await listArtifactsSmart(displayPid);
      setArtifacts(grouped);

      // refresh recents if this was a quick bucket
      if (isQuickPid(displayPid)) {
        if (kind === 'image') setQuickImages(await artifactStore.listRecent(quickId('image'), 'image', 8));
        if (kind === 'video') {
          const vids = await artifactStore.listRecent(quickId('video'), 'video', 8);
          setQuickVideos(await enrichVideoThumbs(vids));
        }
        if (kind === 'audio') setQuickAudio(await artifactStore.listRecent(quickId('audio'), 'audio', 8));
      }
    }
  };


  const handleSaveArtifact = useCallback(async (artifact) => {
    if (!currentProject) return;
    await artifactStore.saveArtifact(currentProject._id, artifact);
    const list = await artifactStore.listArtifacts(currentProject._id);
    setArtifacts(list);
  }, [currentProject]);

  useEffect(() => {
    // Priority: Quick Mode PID -> Current Project PID
    const isQuick = !!workbenchOptions?.quickMode;
    const pid = isQuick ? workbenchOptions?.defaultProjectId : currentProject?._id;

    if (!pid) return setArtifacts({ image: [], video: [], audio: [] });

    listArtifactsSmart(pid).then(setArtifacts).catch(() => { });
  }, [currentProject, workbenchOptions]);

  const loadProjects = async () => {
    try {
      const list = await getProjects();
      const projects = list;

      setProjects(projects);

      // FORCE SYNC: Merge backend workflow state into local state immediately
      setWorkflowState(prev => {
        const next = { ...prev };
        projects.forEach(p => {
          if (p.workflowState) {
            next[p._id] = { ...(next[p._id] || {}), ...p.workflowState };
          }
        });
        return next;
      });

      setProjectsLoaded(true);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error("Error loading projects:", error);
      setProjectsLoaded(true);
    }
  };

  const deselectProject = () => {
    setCurrentProject(null);
    setDetailedScreenplay('');
    setScreenplayOutline(null);
    setIsDetailsGenerating(false);
    setScenes([]);
    setCharacterBible({});
    setArtifactWorkbenchMode(null);
    setMergedVideoUrl(null);
    setIsScreenplayRendered(false);
    setIsScreenplayLoading(false);
  };

  const handleCreateProject = async (project) => {
    const _id = await addProject(project);
    const newProject = {
      ...project,
      _id: _id,
      scenes: [],
      screenplay: '',
      characterBible: ''
    };

    const newProjects = [...projects, newProject];
    setProjects(newProjects);
    setCurrentProject(newProject);

    setIsScreenplayLoading(true);
    handleGenerateScreenplay(newProject);
    setMaximizeScreenPlay(true);

    setWorkflowState(prev => ({
      ...prev,
      [_id]: { ...defaultWorkflow }
    }));
    updateWorkflowState(_id, defaultWorkflow);
  };

  const handleSelectProject = async (project) => {
    setArtifactWorkbenchMode(null);

    if (project == currentProject) {
      deselectProject();
      return;
    }

    if (!project) {
      // Clear state if no project is selected
      setCurrentProject(null);
      setDetailedScreenplay('');
      setCharacterBible(new Map());
      setScenes([]);
      setMaximizeScreenPlay(false);
      setIsScreenplayRendered(false);
      setIsCharacterSlideshow(false);
      setMergedVideoUrl('');
      setSelectedArtifactType(null); // Reset artifact type on project deselect
      return;
    }

    // Existing logic for setting up a selected project
    //setMaximizeScreenPlay(false);
    setCurrentProject(project);
    setMaximizeScreenPlay(true);

    if (project.workflowState) {
      setWorkflowState(prev => {
        const current = prev[project._id];
        const incoming = project.workflowState;

        // Shallow equality check to avoid redundant re-renders
        const hasChange = !current || Object.keys(incoming).some(k =>
          JSON.stringify(incoming[k]) !== JSON.stringify(current[k])
        );

        if (!hasChange) return prev;

        return {
          ...prev,
          [project._id]: { ...(current || {}), ...incoming }
        };
      });
    }

    const outlineFromDb = await getScreenplayOutline(project._id);
    if (outlineFromDb) setScreenplayOutline(outlineFromDb);

    const screenplay = await getScreenplay(project._id);
    setDetailedScreenplay(screenplay);

    let characterBibleMap = new Map();
    try {
      const storedCharacterBible = await getCharacterBible(project._id);
      if (storedCharacterBible instanceof Map && storedCharacterBible.size > 0) {
        characterBibleMap = storedCharacterBible;
      }
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.warn('No character bible for project yet (this is OK).');
      characterBibleMap = new Map();
    }
    setCharacterBible(characterBibleMap);

    const storedScenes = await getScenes(project._id);
    if (storedScenes && storedScenes.length > 0) {
      setScenes(storedScenes);
      setMaximizeScreenPlay(true);
      setIsScreenplayRendered(true);
      setShowScenes(true);
    } else {
      setScenes([]);
      setMaximizeScreenPlay(true);
      setIsScreenplayRendered(true);
      setIsCharacterSlideshow(true);
    }

    setMergedVideoUrl(project.mergedVideoUrl || '');
    setSelectedArtifactType(null); // Reset artifact type on project select

  };

  const handleRemoveProject = async (deletedProjectId) => {
    try {

      // 1) delete on backend
      try {
        // 1) best-effort artifact purge (server + local cache) before project disappears
        await artifactStore.deleteArtifacts(deletedProjectId);
      } finally {
        // 2) delete server-side project
        await deleteProject(deletedProjectId);
      }

      // 2) clear current selection if it’s the one being deleted
      setCurrentProject(prev => (prev && prev._id === deletedProjectId) ? null : prev);
      if (currentProject && currentProject._id === deletedProjectId) {
        // reset view state cleanly
        setDetailedScreenplay('');
        setScenes([]);
        setCharacterBible(new Map());
        setArtifacts({ image: [], video: [], audio: [] });
      }

      // 3) drop from local list
      setProjects(prev => prev.filter(p => p._id !== deletedProjectId));

      // 4) prune workflow cache for this project
      setWorkflowState(prev => {
        if (!prev || !prev[deletedProjectId]) return prev;
        const { [deletedProjectId]: _removed, ...rest } = prev;
        localStorage.setItem('workflowState', JSON.stringify(rest));
        return rest;
      });
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.error('Delete failed:', e);
      alert('Failed to delete project. Please try again.');
    }
  };

  // --- Generates ONLY the screenplay ---
  const handleGenerateScreenplay = async (project, modelAlias) => {
    const delay = (ms) => new Promise((r) => setTimeout(r, ms));


    // --- helper: normalize scenes for tiles/details strip (so no undefined crashes) ---
    const toSceneForTiles = (s, idx) => {
      const safeArray = (x) => (Array.isArray(x) ? x : []);
      const asDialogues = (x) => {
        if (!Array.isArray(x)) return [];
        return x.map((d) => (typeof d === 'string' ? { text: d, audio: null } : d));
      };
      return {
        ...s,
        number: s?.number ?? idx + 1,
        title: s?.title || `Scene ${s?.number ?? idx + 1}`,
        duration: s?.duration || s?.duration_minutes || '',
        summary: s?.summary || s?.synopsis || s?.SceneSynopsis || '',
        image: s?.image || null,
        video: s?.video || null,
        additionalInfo: {
          synopsis: s?.synopsis ?? s?.SceneSynopsis ?? '',
          visual: s?.visualPrompt ?? s?.visual ?? (Array.isArray(s?.visuals) ? s.visuals.join('\n') : ''),
          visualPrompt: s?.visualPrompt ?? '',
          sceneImagePrompt: s?.sceneImagePrompt || s?.scene_image_prompt || '',
          environment: s?.environment ?? '',
          characterExperessions: safeArray(s?.characterExperessions),
          characterAttire: safeArray(s?.characterAttire),
          //characters: safeArray(s?.characters),
          //beats: safeArray(s?.beatSummary ?? s?.beats),
          //shotsPerBeat: safeArray(s?.shotsPerBeat),
          coveragePlan: s?.coveragePlan ?? null,
          lightingPlan: s?.lightingPlan ?? (Array.isArray(s?.lighting) ? s.lighting.join('\n') : ''),
          fx: s?.fx ?? '',
          dialogues: asDialogues(s?.dialogues),
          music: { text: s?.backgroundMusic || '', audio: null },
          sceneBible: s?.sceneBible ?? s?.scene_bible ?? null,
          shotMatrix: safeArray(s?.shotMatrix ?? s?.shot_matrix),
          //...(s?.scene_bible ? { scene_bible: s.scene_bible } : {}),
          //...(Array.isArray(s?.shot_matrix) && s.shot_matrix.length ? { shot_matrix: s.shot_matrix } : {})
        },
      };
    };

    setIsScreenplayLoading(true);
    setIsScreenplayRendered(false);
    setHydratedByNumber({}); // clear progressive map
    try {
      // Build story object (unchanged)
      const story = { title: project.title, description: project.story ?? project.description };

      // Choose model (unchanged logic)
      const textBucket = modelOptions?.text || [];
      const fallback = textBucket.find((m) => m.default)?.alias || textBucket[0]?.alias || 'gpt-5';
      const chosenModelAlias = modelAlias || workflowState?.[project._id]?.screenplay?.model || fallback;

      // 1) Generate OUTLINE for ALL scenes (strict JSON) and start details job
      await metering.runScreenplay(
        { projectId: project._id, meta: { source: 'narrative-workbench', model: chosenModelAlias } },
        async () => {
          const outlineResp = await generateScreenplayOutline(story, chosenModelAlias, /* backgroundDetails */ true);
          const outline = outlineResp?.screenplayOutline || null;

          if (!outline || !Array.isArray(outline.scenes) || outline.scenes.length === 0) {
            throw new Error('Outline generation returned no scenes');
          }

          // show outline immediately (all sp-cards)
          setScreenplayOutline(outline);

          try { await saveScreenplayOutline(project._id, outline); } catch (e) { console.warn('Outline persist failed (non-fatal):', e); }

          setWorkflowState(prev => ({
            ...prev,
            [project._id]: {
              ...(prev[project._id] || {}),
              screenplay: { ...(prev[project._id]?.screenplay || {}), outlineDone: true, detailsDone: false, complete: false }
            }
          }));

          setIsScreenplayRendered(true);
          setIsScreenplayLoading(false);

          const currentWF = workflowStateRef.current[project._id] || {};
          await updateWorkflowState(project._id, {
            ...currentWF,
            screenplay: {
              ...(currentWF.screenplay || {}),
              outlineDone: true,    // <- used by ProjectSidebar gating
              // complete stays false until details are finished
            }
          });

          //fire-and-forget Character Bible generation as soon as outline is done
          if (!autoCBTriggeredRef.current) {
            const cbAlreadyComplete = !!workflowState?.[project._id]?.characterBible?.complete;
            if (!cbAlreadyComplete && typeof handleGenerateCharacterBible === 'function') {
              autoCBTriggeredRef.current = true;
              // don't await — keep screenplay details flow independent
              setTimeout(() => {
                try {
                  handleGenerateCharacterBible(project, chosenModelAlias);
                } catch (e) {
                  // no-op; button is still available for manual retry
                  if (process.env.NODE_ENV !== 'production') console.warn('Auto Character Bible failed:', e);
                  autoCBTriggeredRef.current = false; // allow manual trigger
                }
              }, 0);
            }
          }


          // 2) Poll strict-JSON details job → progressively hydrate
          const jobId = outlineResp?.detailJobId;
          if (jobId) {
            setIsDetailsGenerating(true);
            let done = false;
            while (!done) {
              let j;
              try {
                j = await getScreenplayDetailJob(jobId);
              } catch {
                j = null;
              }

              if (j && (j.status === 'running' || j.status === 'queued')) {
                const partial = j.partial || {};
                if (partial && typeof partial === 'object') {
                  if (isMounted.current) setHydratedByNumber((prev) => ({ ...prev, ...partial }));
                }
              } else if (j && j.status === 'done') {
                // ---------- JOB COMPLETE: run ALL the old logic you used to do ----------
                const jsonScreenplay = j.result?.screenplay || { title: outline.title || story.title || '', scenes: [] };


                // 1) Persist JSON **first** (source of truth)
                await saveScreenplay(project._id, jsonScreenplay);
                if (isMounted.current) setDetailedScreenplay(jsonScreenplay);

                // 2) Build scenes for tiles **directly from JSON**
                const parsedFromJson = normalizeScenesFromJson(jsonScreenplay);        // NEW
                const normalizedScenes = parsedFromJson.map(toSceneForTiles);          // unchanged adapter

                // 3) Push to state so UI shows full hydrated set
                setScenes(normalizedScenes);

                // 4) Persist scene details like before
                await saveScenes(project._id, normalizedScenes);

                // 5) Provide ScreenplayTile with JSON (no need to parse back from text)
                setHydratedByNumber(() => {
                  const byNum = {};
                  (jsonScreenplay.scenes || []).forEach((s) => {
                    const k = String(s?.number ?? '');
                    if (k) byNum[k] = s;
                  });
                  return byNum;
                });


                // 7) Update workflow (server + local)
                const currentWF = workflowStateRef.current[project._id] || {};
                const updatedWF = {
                  ...currentWF,
                  screenplay: {
                    ...(currentWF.screenplay || {}),
                    outlineDone: true,
                    detailsDone: true,
                    complete: true,
                    model: chosenModelAlias
                  }
                };

                await updateWorkflowState(project._id, updatedWF);

                setWorkflowState(prev => ({
                  ...prev,
                  [project._id]: updatedWF,
                }));

                done = true;
              } else if (j && j.status === 'error') {
                // Keep outline; user can still work with hydrated subset if any

                done = true;
              }

              if (!done) await delay(5000);
            }
            setIsDetailsGenerating(false);
            setIsDetailsGenerating(false);
          } else {
            // Shouldn't happen (we requested backgroundDetails), but keep UX smooth
            setIsDetailsGenerating(false);
          }

        } // end async runCore
      ); // end metering.runScreenplay
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error generating screenplay outline/details:', error);
    } finally {
      setIsScreenplayLoading(false);
    }
  };


  // --- Generates ONLY the character bible (minimal, resilient, with 1 retry) ---
  const handleGenerateCharacterBible = async (project, modelAlias = 'gpt-5') => {
    setIsCharacterBibleLoading(true);
    try {
      const outline = screenplayOutline ?? (await getScreenplayOutline(project._id).catch(() => null));

      if (!outline) {
        // last resort: derive from existing detailed text to preserve current behavior
        const screenplayText = detailedScreenplay || (await getScreenplay(project._id));
        var screenplayLite = buildScreenplayLite(screenplayText);
      }

      const storyRaw = (project?.story ?? project?.description ?? '').toString();
      const storySnippet = storyRaw.length > 3000 ? storyRaw.slice(0, 3000) : storyRaw;

      const { characterMap, elapsed_ms } = await metering.runCharacterBibleAdjusted(
        {
          projectId: project._id,
          unitsMax: 10,
          meta: { source: 'narrative-workbench', model: modelAlias },
          calcUnits: (res) => {
            try { return Object.keys(res?.characterMap || {}).length || 1; }
            catch { return 1; }
          },
        },
        async () => {
          const titleObj = { title: project?.title || 'Untitled', description: storyRaw };
          const payload = {
            titleObj,
            model: modelAlias,
            outline: outline || screenplayLite,
            styleGuide: 'Realistic, IMAX-grade; grounded physics; Nolan-esque tone.',
          };
          const strict = await generateCharacterBibleStrict(payload);
          return { characterMap: strict?.characterMap || {}, elapsed_ms: strict?.elapsed_ms || 0 };
        }
      );

      const toSummary = (entry = {}) => summarizeStrictCharacter(entry);


      const cbMap = new Map();
      Object.entries(characterMap || {}).forEach(([name, entry]) => {
        cbMap.set(name, {
          description: toSummary(entry),
          conciseDescription: entry.concise_description || entry.conciseDescription || '',
          image: entry.image || null,
          // Store the full strict object for future detailed UIs (opaque blob)
          _strict: entry
        });
      });

      setCharacterBible(cbMap);
      await saveCharacterBible(project._id, cbMap);

      setIsCharacterEnriching(true);

      (async () => {
        try {
          const payload = { projectId: project._id, characterMap: Object.fromEntries(cbMap) };
          const { characterMap: enriched } = await enrichCharacters(payload);
          // Patch entries progressively (same logic you already had)
          if (!isMounted.current) return;
          setCharacterBible(prev => {
            const next = new Map(prev);
            Object.entries(enriched || {}).forEach(([name, updated]) => {
              const cur = next.get(name) || {};
              next.set(name, {
                ...cur,
                ...(Object.prototype.hasOwnProperty.call(updated, 'image') ? { image: updated.image } : {}),
                ...(Object.prototype.hasOwnProperty.call(updated, 'conciseDescription') ? { conciseDescription: updated.conciseDescription } : {})
              });
            });
            // persist progressive improvements (unchanged behavior)
            saveCharacterBible(project._id, next).catch(() => { });
            return next;
          });
        } catch (e) {
          // ignore — UI remains usable with strict data
        } finally {
          setIsCharacterEnriching(false);

          // Use Ref to get latest state (avoid stale closure)
          const currentWF = workflowStateRef.current[project._id] || {};
          const updatedWF = {
            ...currentWF,
            characterBible: { ...currentWF.characterBible, complete: true }
          };

          setWorkflowState((prev) => ({
            ...prev,
            [project._id]: updatedWF,
          }));

          updateWorkflowState(project._id, updatedWF);
        }
      })();


      const hasErrors = Array.from(cbMap.values()).some(
        (v) => !v.image || !v.conciseDescription
      );

      // Moved completion logic to inside the async enrichment block above
      // setWorkflowState(...) 
      // updateWorkflowState(...)

      setIsCharacterSlideshow(true);

    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error generating character bible:', error);
      }
    } finally {
      setIsCharacterBibleLoading(false);
    }
  };


  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  const retry = async (fn, { retries = 1, wait = 300 } = {}) => {
    let lastErr;
    for (let i = 0; i <= retries; i++) {
      try { return await fn(); }
      catch (e) { lastErr = e; if (i < retries) await sleep(wait); }
    }
    throw lastErr;
  };

  // Bug Fix: Robust polling with timeout to prevent infinite loops
  const pollJobSafe = async (jobId, maxAttempts = 150, interval = 4000) => {
    for (let i = 0; i < maxAttempts; i++) {
      const job = await getVideoJob(jobId);
      if (job?.state === 'SUCCEEDED') return job;
      if (job?.state === 'FAILED') throw new Error(job?.error || 'Video job failed');
      await new Promise(r => setTimeout(r, interval));
    }
    throw new Error('Video generation timed out');
  };

  const toB64IfUrl = async (maybe) => /^https?:/i.test(maybe) ? blobToBase64(await downloadToBlob(maybe)) : maybe;

  const getSelectedScenesImageModel = React.useCallback(() => {
    const bucket = modelOptions?.image || [];
    const fallback = (bucket.find(m => m.default)?.alias) || (bucket[0]?.alias) || 'gpt-image-1';
    return workflowState?.[currentProject?._id]?.scenes?.model || fallback;
  }, [modelOptions?.image, workflowState, currentProject?._id]);



  const handleGenerateScenes = async () => {
    setIsScenesGenerating(true);
    setLoading(true);
    try {
      const context = `This is a continuous narrative for the story titled "${currentProject.title}". 
        All scenes should have a consistent style and theme, and should be shot from the same perspective, maintaining the same color palette and artistic style. 
        Generate an image accurately depicting the scene based on the information below.`;


      const toSceneForTiles = (s, idx) => {
        const safeArray = (x) => (Array.isArray(x) ? x : []);
        const asDialogues = (x) => {
          if (!Array.isArray(x)) return [];
          return x.map((d) => (typeof d === 'string' ? { text: d, audio: null } : d));
        };
        return {
          ...s,
          number: s?.number ?? idx + 1,
          title: s?.title || `Scene ${s?.number ?? idx + 1}`,
          duration: s?.duration || s?.duration_minutes || '',
          summary: s?.summary || s?.synopsis || s?.SceneSynopsis || '',
          image: s?.image || null,
          video: s?.video || null,
          characters: safeArray(s?.characters),
          additionalInfo: {
            synopsis: s?.synopsis ?? s?.SceneSynopsis ?? '',
            visual: s?.visualPrompt ?? s?.visual ?? (Array.isArray(s?.visuals) ? s.visuals.join('\n') : ''),
            visualPrompt: s?.visualPrompt ?? s?.visual ?? (Array.isArray(s?.visuals) ? s.visuals.join('\n') : ''),
            sceneImagePrompt: s?.sceneImagePrompt || s?.scene_image_prompt || '',
            environment: s?.environment ?? '',
            characterExperessions: safeArray(s?.characterExperessions),
            characterAttire: safeArray(s?.characterAttire),
            //beats: safeArray(s?.beatSummary ?? s?.beats),
            //shotsPerBeat: safeArray(s?.shotsPerBeat),
            coveragePlan: s?.coveragePlan ?? null,
            lightingPlan: s?.lightingPlan ?? (Array.isArray(s?.lighting) ? s.lighting.join('\n') : ''),
            fx: s?.fx ?? '',
            dialogues: asDialogues(s?.dialogues),
            music: { text: s?.backgroundMusic || '', audio: null },
            sceneBible: s?.sceneBible ?? s?.scene_bible ?? null,
            shotMatrix: safeArray(s?.shotMatrix ?? s?.shot_matrix),
            //...(s?.scene_bible ? { scene_bible: s.scene_bible } : {}),
            //...(Array.isArray(s?.shot_matrix) && s.shot_matrix.length ? { shot_matrix: s.shot_matrix } : {})
          },
        };
      };


      const parsedFromJson = normalizeScenesFromJson(detailedScreenplay);
      const parsedScenes = parsedFromJson.map(toSceneForTiles);




      const runCore = async () => {
        const scenePromises = parsedScenes.map(async (scene) => {
          // Build concise character string once (guarded)
          let characterBibleString = '';
          try {
            characterBibleString = generateCharacterDescriptionsConcise(scene.characters, characterBible);
          } catch (_) {
            // fall back softly
            try { characterBibleString = generateCharacterDescriptions(scene.characters, characterBible); } catch { }
          }

          const charImgmap = normalizeCharacterBible(characterBible, scene.characters);

          const selectedImageModel = getSelectedScenesImageModel();

          // ── 1) IMAGE (retry x2) ─────────────────────────────────────────────
          let imageCoverUrl = null;
          let imageError = null;
          try {
            const raw = await retry(() => ImageAPI.sceneWithRefs({
              sceneDescription: scene.sceneImagePrompt || scene.visual || scene.visualPrompt,
              context,
              characterBible: charImgmap,
              environment: scene.environment,
              characterExpressions: scene.characterExperessions ?? scene.characterExpressions,
              characterAttire: scene.characterAttire,
              model: selectedImageModel,
              scene_number: scene.number
            }), { retries: 2, wait: 400 });

            const imageB64 = await toB64IfUrl(raw);

            imageCoverUrl = await retry(
              () => saveImageOnCloudB64(imageB64, `${currentProject._id}_scene_${scene.number}`, currentProject._id, 'scenes'),
              { retries: 1, wait: 300 }
            );
          } catch (e) {
            imageError = e?.message || 'Image generation/upload failed';
          }

          // ── 2) AUDIO DIALOGUES (per line guarded; small retries) ────────────
          const dialoguesWithAudio = await Promise.all(scene.dialogues.map(async (dialogue, index) => {
            try {
              const formattedDialog = dialogue.split(':').pop();
              const audioURL = await retry(() => getSpeechFromText(formattedDialog), { retries: 1, wait: 300 });

              // Convert & upload (each with a retry)
              const audioBlob = await retry(() => downloadToBlob(audioURL), { retries: 1, wait: 250 });
              const audioFile = new File([audioBlob], `${currentProject._id}_audio_${scene.number}_${index}.mp3`, { type: "audio/mpeg" });
              const audioCloudURL = await retry(
                () => saveAudioOnCloud(audioFile, `${currentProject._id}_audio_${scene.number}_${index}`),
                { retries: 1, wait: 250 }
              );

              return { text: dialogue, audio: audioCloudURL };
            } catch (e) {
              // degrade gracefully: keep text, no audio
              return { text: dialogue, audio: null, error: e?.message || 'Dialogue TTS/upload failed' };
            }
          }));

          // ── 3) BACKGROUND MUSIC (retry x1) ──────────────────────────────────
          let musicURL = null;
          let musicError = null;
          try {
            musicURL = await retry(() => generateMusicFromPrompt(scene.backgroundMusic), { retries: 1, wait: 300 });
          } catch (e) {
            musicError = e?.message || 'Music generation failed';
          }

          // Optional: if you intended to upload music, do it here similarly with retry()

          // ── Final scene object (even with partial failures) ─────────────────
          return {
            number: scene.number,
            image: imageCoverUrl,                    // null if failed
            title: `Scene ${scene.number}: ${scene.title}`,
            duration: scene.duration,
            summary: scene.synopsis.substring(0, 200) + '...',
            additionalInfo: {
              synopsis: scene.synopsis || '',
              visual: scene.visual || scene.visualPrompt,
              setting: scene.settingDetails,
              environment: scene.environment,
              characterExperessions: scene.characterExperessions,
              characterAttire: scene.characterAttire,
              dialogues: dialoguesWithAudio,
              music: { text: scene.backgroundMusic, audio: musicURL, error: musicError || null },
              context,
              characters: scene.characters,
              //beats: scene.beatSummary || [],
              //shotsPerBeat: scene.shotsPerBeat || [],
              sceneBible: scene.sceneBible || null,
              shotMatrix: scene.shotMatrix || [],
              coveragePlan: scene.coveragePlan || { coverage: '', transitions: '', editorialIntent: '' },
              lightingPlan: scene.lightingPlan || '',
              fx: scene.fx || '',
            },
            errors: {
              image: imageError || null
            },
            video: null
          };
        });

        // Don’t let one failure cancel the batch
        const results = await Promise.allSettled(scenePromises);

        let sceneData = results.map((r, i) => {
          if (r.status === 'fulfilled') return r.value;
          // extreme fallback for a totally failed scene task
          const s = parsedScenes[i];
          return {
            number: s.number,
            image: null,
            title: `Scene ${s.number}: ${s.title}`,
            duration: s.duration,
            summary: s.synopsis.substring(0, 200) + '...',
            additionalInfo: {
              synopsis: s.synopsis || '',
              visual: s.visual || s.visualPrompt,
              setting: s.settingDetails,
              environment: s.environment,
              characterExperessions: s.characterExperessions,
              characterAttire: s.characterAttire,
              dialogues: [],               // nothing generated
              music: { text: s.backgroundMusic, audio: null, error: r.reason?.message || 'Scene generation failed' },
              context,
              characters: s.characters,
              //beats: s.beatSummary || [],
              //shotsPerBeat: s.shotsPerBeat || [],
              sceneBible: s.sceneBible || null,
              shotMatrix: s.shotMatrix || [],
            },
            errors: { scene: r.reason?.message || 'Scene generation failed' },
            video: null
          };
        });


        // ── 4) SHOT IMAGES (per scene; uploads to GCS, patches shotMatrix) ────
        try {
          const sceneDataWithShots = await Promise.all(sceneData.map(async (sceneObj) => {
            const shotMatrix = sceneObj?.additionalInfo?.shotMatrix || [];
            if (!Array.isArray(shotMatrix) || shotMatrix.length === 0) return sceneObj;

            // Shape scene payload for backend (expects top-level `shot_matrix`)
            const scenePayload = {
              number: sceneObj.number,
              title: sceneObj.title,
              visual: sceneObj?.additionalInfo?.visual || '',
              environment: sceneObj?.additionalInfo?.environment || '',
              characters: Array.isArray(sceneObj?.additionalInfo?.characters) ? sceneObj.additionalInfo.characters : [],
              background_music: sceneObj?.additionalInfo?.music?.text || '',
              scene_bible: sceneObj?.additionalInfo?.sceneBible || sceneObj?.additionalInfo?.scene_bible || null,
              character_attire: sceneObj?.additionalInfo?.characterAttire || sceneObj?.additionalInfo?.character_attire || null,
              dialogues: Array.isArray(sceneObj?.additionalInfo?.dialogues)
                ? sceneObj.additionalInfo.dialogues.map((d) => {
                  if (typeof d === 'string') return { line: d };
                  const text = d?.text ?? d?.line ?? '';
                  return d?.character ? { character: d.character, line: text } : { line: text };
                })
                : [],
              shot_matrix: shotMatrix,
            };

            let apiResp;
            try {
              //const charBibleString = generateCharacterDescriptionsConcise(sceneObj.additionalInfo?.characters, characterBible);
              const charmap = normalizeCharacterBible(characterBible);
              //apiResp = await generateSceneShotImageEdits({ scene: scenePayload, projectId: currentProject._id, characterBible: charmap });
            } catch (e) {
              if (process.env.NODE_ENV !== 'production') console.warn('generateSceneShotImageEdits failed for scene', sceneObj.number, e);
              return sceneObj;
            }

            const results = Array.isArray(apiResp?.results) ? apiResp.results : [];
            const beatMap = new Map(results.map((b) => [String(b?.beat ?? ''), b]));

            const updatedMatrix = await Promise.all(shotMatrix.map(async (beatRow) => {
              const rBeat = beatMap.get(String(beatRow?.beat ?? ''));
              if (!rBeat) return beatRow;
              const rShots = Array.isArray(rBeat.shots) ? rBeat.shots : [];

              const updatedShots = await Promise.all((Array.isArray(beatRow.shots) ? beatRow.shots : []).map(async (shot) => {
                const match = rShots.find((rs) => String(rs?.id ?? '') === String(shot?.id ?? ''));
                const data = match?.b64 || match?.url || '';
                if (!data) return shot;
                try {
                  const b64 = await toB64IfUrl(data);
                  const key = `${currentProject._id}_scene_${sceneObj.number}_beat_${beatRow.beat}_shot_${shot.id}`;
                  const url = await saveImageOnCloudB64(b64, key, currentProject._id, 'scenes');
                  return { ...shot, imageURL: url, imageId: null };
                } catch (e) {
                  if (process.env.NODE_ENV !== 'production') console.warn('upload shot image failed', e);
                  return shot;
                }
              }));

              return { ...beatRow, shots: updatedShots };
            }));

            return { ...sceneObj, additionalInfo: { ...sceneObj.additionalInfo, shotMatrix: updatedMatrix } };
          }));

          sceneData = sceneDataWithShots;
        } catch (e) {
          if (process.env.NODE_ENV !== 'production') console.warn('shot image pass failed', e);
        }


        setScenes(sceneData);
        await saveScenes(currentProject._id, sceneData);
        setShowScenes(true);

        const currentWF = workflowStateRef.current[currentProject._id] || {};
        const updatedWF = {
          ...currentWF,
          scenes: { ...(currentWF.scenes || {}), complete: true }
        };

        setWorkflowState(prev => ({
          ...prev,
          [currentProject._id]: updatedWF
        }));
        updateWorkflowState(currentProject._id, updatedWF);

        // return a count for metering settlement
        const successfulCount = results.filter(r => r.status === 'fulfilled').length;
        return { successfulCount };
      };

      // ── METERING: reserve max (planned scenes), run, then debit actual ──────
      await metering.runScenesAdjusted(
        {
          projectId: currentProject._id,
          unitsMax: parsedScenes.length || 1,
          meta: { source: 'narrative-workbench', step: 'scenes' },
          calcUnits: (res) => res?.successfulCount || 1,
        },
        runCore
      );

    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error generating scenes:', error);
    } finally {
      setIsScenesGenerating(false);
      setLoading(false);
    }
  };


  const handleLogout = () => {

    const uid = localStorage.getItem('userId') || 'guest';
    localStorage.removeItem(LS_QUICK_IDS(uid));
    Object.keys(localStorage)
      .filter(k => k.startsWith(`artifacts:${uid}:`))
      .forEach(k => localStorage.removeItem(k));

    localStorage.removeItem('auth');  // Clear authentication flag
    localStorage.removeItem('userId'); // Clear userId
    localStorage.removeItem('token');

    navigate('/login'); // Redirect to the login page
  };


  // NEW: Track individual image regeneration for UI feedback
  const [imageIsRegenerating, setImageIsRegenerating] = useState({});

  const regenerateImage = useCallback(async (sceneIndex, modelOverride = null) => {
    try {
      setImageIsRegenerating(prev => ({ ...prev, [sceneIndex]: true }));
      const scene = scenes[sceneIndex];
      if (!scene) throw new Error("Invalid scene index");

      const charImgmap = normalizeCharacterBible(characterBible, scene.characters);
      const selectedImageModel = modelOverride || getSelectedScenesImageModel();

      const sceneNumber = scene?.number ?? (sceneIndex + 1);
      const url = await metering.runSceneImage(
        { projectId: currentProject?._id, sceneNumber, meta: { source: 'narrative-workbench', action: 'regenerate' } },
        async () => {
          const raw = await retry(() => ImageAPI.sceneWithRefs({
            sceneDescription: scene.additionalInfo.visual || scene.visualPrompt,
            context: scene.additionalInfo.context,
            characterBible: charImgmap,
            environment: scene.additionalInfo.environment,
            characterExpressions: scene.additionalInfo.characterExperessions ?? scene.additionalInfo.characterExpressions, // typo-safe
            model: selectedImageModel,
            characterAttire: scene.additionalInfo.characterAttire,
            scene_number: sceneNumber
          }), { retries: 2, wait: 400 });

          const b64 = await toB64IfUrl(raw);

          return await retry(
            () => saveImageOnCloudB64(b64, `scene_${sceneIndex}_${Date.now()}`, currentProject?._id, 'scenes'),
            { retries: 1, wait: 300 }
          );
        }
      );
      const pid = currentProject?._id;

      setScenes(prev => {
        const next = [...prev];
        if (!next[sceneIndex]) return prev;   // list changed mid-flight

        // History: keep unique images
        const oldHistory = Array.isArray(next[sceneIndex].imageHistory) ? next[sceneIndex].imageHistory : [];
        const newHistory = [...new Set([...oldHistory, next[sceneIndex].image, url])].filter(Boolean); // keep valid urls

        next[sceneIndex] = {
          ...next[sceneIndex],
          image: url,
          imageHistory: newHistory
        };
        saveScenes(pid, next);                // persist the same snapshot
        return next;
      });
    } catch (err) {
      alert('Failed to regenerate scene image: ' + (err?.message || err));
      if (process.env.NODE_ENV !== 'production') console.error('regenerateImage error:', err);
    } finally {
      setImageIsRegenerating(prev => ({ ...prev, [sceneIndex]: false }));
    }
  }, [scenes, characterBible, currentProject?._id, getSelectedScenesImageModel]);

  const regenerateImagePostEdit = useCallback(async (sceneIndex, newVisualPrompt) => {
    try {
      setImageIsRegenerating(prev => ({ ...prev, [sceneIndex]: true }));
      const scene = scenes[sceneIndex];
      if (!scene) throw new Error("Invalid scene index");

      const characterBibleString = generateCharacterDescriptions(
        scene.additionalInfo.characters,
        characterBible
      );

      const selectedImageModel = getSelectedScenesImageModel();

      const sceneNumber = scene?.number ?? (sceneIndex + 1);
      const coverImageURL = await metering.runSceneImage(
        { projectId: currentProject?._id, sceneNumber, meta: { source: 'narrative-workbench', action: 'regenerate_post_edit' } },
        async () => {
          const b64 = await toB64IfUrl(await ImageAPI.scene({
            sceneDescription: scene.additionalInfo.visual,   // (using current visual; if you want to use newVisualPrompt, change this line)
            context: scene.additionalInfo.context,
            characterBible: characterBibleString,
            environment: scene.additionalInfo.environment,
            characterExpressions: scene.additionalInfo.characterExperessions ?? scene.additionalInfo.characterExpressions,
            model: selectedImageModel
          }));
          return await saveImageOnCloudB64(b64, `scene_${sceneIndex}_${Date.now()}`, currentProject?._id, 'scenes');
        }
      );
      const pid = currentProject?._id;

      setScenes(prev => {
        const next = [...prev];
        if (!next[sceneIndex]) return prev;

        // History: keep unique images
        const oldHistory = Array.isArray(next[sceneIndex].imageHistory) ? next[sceneIndex].imageHistory : [];
        const newHistory = [...new Set([...oldHistory, next[sceneIndex].image, coverImageURL])].filter(Boolean);

        next[sceneIndex] = {
          ...next[sceneIndex],
          image: coverImageURL,
          imageHistory: newHistory,
          additionalInfo: {
            ...next[sceneIndex].additionalInfo,
            visual: newVisualPrompt,
          },
        };
        saveScenes(pid, next);
        return next;
      });
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error regenerating image:', error);
    } finally {
      setImageIsRegenerating(prev => ({ ...prev, [sceneIndex]: false }));
    }
  }, [scenes, characterBible, currentProject?._id, getSelectedScenesImageModel]);

  const handleSelectSceneImage = useCallback((sceneIndex, url) => {
    setScenes(prev => {
      const next = [...prev];
      if (!next[sceneIndex]) return prev;
      next[sceneIndex] = { ...next[sceneIndex], image: url };
      saveScenes(currentProject?._id, next);
      return next;
    });
  }, [currentProject?._id]);


  const getSelectedVideoModel = React.useCallback(() => {
    const bucket = modelOptions?.video || [];
    const fallback = bucket.find(m => m.default)?.alias || bucket[0]?.alias || 'veo';
    const saved = workflowState?.[currentProject?._id]?.videos?.model;
    return saved || fallback;           // prefer saved even if not in today's list
  }, [modelOptions?.video, workflowState, currentProject?._id]);


  function collectSceneShotVideosOrdered(scene) {
    const matrix = scene?.additionalInfo?.shotMatrix || [];
    // sort beats numerically, then shots lexicographically by id
    const rows = [...matrix].sort((a, b) => Number(a?.beat || 0) - Number(b?.beat || 0));
    const out = [];
    rows.forEach(row => {
      const shots = Array.isArray(row?.shots) ? [...row.shots] : [];
      shots.sort((a, b) => String(a?.id || '').localeCompare(String(b?.id || '')));
      shots.forEach(s => { if (s?.videoUrl) out.push(s.videoUrl); });
    });
    return out;
  }

  async function fetchFreshScene(projectId, scene) {
    // pull the latest scenes from DB (Mongo-first in your stack), then pick ours
    const all = await getScenes(projectId);
    const byId = scene?._id ? all.find(s => s._id === scene._id) : null;
    return byId || all.find(s => s.number === scene?.number) || scene;
  }


  const generateVideo = useCallback(async (index) => {
    if (videoIsLoading[index]) return;

    const pid = currentProject?._id;
    let reservationId;
    try {
      setVideoIsLoading(prev => ({ ...prev, [index]: true }));

      const scene = scenes[index];
      if (!scene) throw new Error("Invalid scene index");

      // 1) Prepare strict scene payload like the images pass does
      const shotMatrix = scene?.additionalInfo?.shotMatrix || [];
      if (!Array.isArray(shotMatrix) || !shotMatrix.length) {
        throw new Error("No shot matrix on this scene");
      }

      const scenePayload = {
        number: scene.number,
        title: scene.title,
        visual: scene?.additionalInfo?.visual || '',
        environment: scene?.additionalInfo?.environment || '',
        characters: Array.isArray(scene?.additionalInfo?.characters) ? scene.additionalInfo.characters : [],
        background_music: scene?.additionalInfo?.music?.text || '',
        scene_bible: scene?.additionalInfo?.sceneBible || scene?.additionalInfo?.scene_bible || null,
        //character_attire: scene?.additionalInfo?.characterAttire || scene?.additionalInfo?.character_attire || null,
        dialogues: Array.isArray(scene?.additionalInfo?.dialogues)
          ? scene.additionalInfo.dialogues.map(d => {
            if (typeof d === 'string') return { line: d };
            const text = d?.text ?? d?.line ?? '';
            return d?.character ? { character: d.character, line: text } : { line: text };
          })
          : [],
        shot_matrix: shotMatrix,
      };

      // 2) Submit batch of per-shot video jobs
      const model = getSelectedVideoModel();
      const charmap = normalizeCharacterBible(characterBible, scenePayload.characters); // FE → backend-friendly map :contentReference[oaicite:3]{index=3}
      const options = { duration: 8 }; // matches backend default; tweak if needed

      const totalShots =
        (Array.isArray(shotMatrix) ? shotMatrix : [])
          .reduce((acc, row) => acc + (Array.isArray(row?.shots) ? row.shots.length : 0), 0) || 1;
      const secondsPerShot = Math.max(1, Number(options?.duration || 8));
      const maxUnits = secondsPerShot * totalShots;

      const r = await metering.reserveSceneVideo({
        projectId: pid,
        sceneNumber: scene?.number ?? (index + 1),
        maxUnits,
        meta: { source: 'narrative-workbench', mode: 'per-shot-batch' },
      });
      reservationId = r?.reservationId;

      let resp;
      try {
        resp = await generateSceneShotVideos({
          projectId: pid,
          scene: scenePayload,
          characterBible: charmap,
          options,
          model
        });
      } catch (e) {
        if (reservationId) {
          await metering.cancelSceneVideo(reservationId, 'outer_failure').catch(() => { });
        }
        throw e;
      }

      // Expected shape: { results: [{ beat, shots: [{ id, jobId, state, ... }]}, ...] }
      const results = Array.isArray(resp?.results) ? resp.results : [];
      if (!results.length) throw new Error("Video job submission returned no shots");

      // Build index to map jobId -> {beatIdx, shotIdx}
      const jobIndex = new Map();
      results.forEach((beatBlock, rIdx) => {
        // Backend returns results in same order as shot_matrix, so we use index directly
        const rowIdx = rIdx;

        (Array.isArray(beatBlock?.shots) ? beatBlock.shots : []).forEach((s, sIdx) => {
          if (!s?.jobId) return;
          // Backend returns shots in same order as input list, so we use index matching
          // This avoids issues with missing sets or duplicate IDs
          const shotIdx = sIdx;

          if (rowIdx >= 0 && shotIdx >= 0) {
            jobIndex.set(s.jobId, { rowIdx, shotIdx });
          }
        });
      });

      // 3) Poll all jobs; on SUCCEEDED, write url into the exact shot
      const pending = new Set(jobIndex.keys());
      const successfulUrls = new Map(); // Fix: Track locally to avoid DB read-after-write race

      let attempts = 0;
      const MAX_ATTEMPTS = 150; // ~10 minutes

      while (pending.size && attempts < MAX_ATTEMPTS) {
        attempts++;
        // Fix: Rotate polling selection to prevent starvation if > 6 jobs
        const pendingArray = Array.from(pending);
        const batchSize = 6;
        const startIndex = (attempts * batchSize) % pendingArray.length;

        let batch;
        if (pendingArray.length <= batchSize) {
          batch = pendingArray;
        } else {
          // simple wrap-around slice
          batch = [];
          for (let k = 0; k < batchSize; k++) {
            batch.push(pendingArray[(startIndex + k) % pendingArray.length]);
          }
        }
        // Deduplicate
        batch = [...new Set(batch)];
        const statuses = await Promise.all(batch.map(async (jobId) => {
          try { return await getVideoJob(jobId); } catch { return null; }
        }));

        for (let i = 0; i < batch.length; i++) {
          const jobId = batch[i];
          const j = statuses[i];

          if (!j) continue;

          if (j.state === 'FAILED') {
            console.warn(`Video job ${jobId} failed during polling:`, j.error);
            pending.delete(jobId);
            continue;
          }

          if (j.state === 'SUCCEEDED' && j.url) {

            successfulUrls.set(jobId, j.url); // Track success locally

            const { rowIdx, shotIdx } = jobIndex.get(jobId) || {};
            if (rowIdx != null && shotIdx != null) {
              // in-place immutable update of the single shot’s videoUrl
              setScenes(prev => {
                const next = [...prev];
                const sc = next[index];
                if (!sc) return prev;

                const rows = (sc.additionalInfo?.shotMatrix || []).map(r => ({ ...r, shots: [...(r.shots || [])] }));
                const shot = rows[rowIdx]?.shots?.[shotIdx];
                if (shot) rows[rowIdx].shots[shotIdx] = { ...shot, videoUrl: j.url };

                const updated = {
                  ...sc,
                  additionalInfo: { ...(sc.additionalInfo || {}), shotMatrix: rows }
                };

                next[index] = updated;
                retry(() => saveScenes(pid, next), { retries: 1, wait: 300 }).catch(() => { });
                return next;
              });
            }
            pending.delete(jobId);
          }
        }

        if (pending.size) {
          await new Promise(r => setTimeout(r, 4000)); // reuse your existing cadence
        }
      }

      // Reconstruct ordered list from local success map (avoiding stale DB fetch)
      const locallyCollectedShots = [];
      const latestMatrix = scenePayload.shot_matrix || []; // use original payload structure
      latestMatrix.forEach((r, rIdx) => {
        (Array.isArray(r.shots) ? r.shots : []).forEach((s, sIdx) => {
          // We need to find the job ID for this shot to check headers
          // iterating results to find matching result->shot->jobId
          // Optimization: we could built a reverse map, but double loop is fine for small count
          let foundUrl = null;
          // Find in jobIndex values
          for (const [jid, coords] of jobIndex.entries()) {
            if (coords.rowIdx === rIdx && coords.shotIdx === sIdx) {
              foundUrl = successfulUrls.get(jid);
              break;
            }
          }
          if (foundUrl) {
            locallyCollectedShots.push({ ...s, videoUrl: foundUrl });
          }
        });
      });


      // ---- Compute successful units and commit metering
      const succeededShots = successfulUrls.size;
      const usedUnits = secondsPerShot * Math.max(0, succeededShots);
      if (usedUnits > 0) {
        await metering.commitSceneVideo(reservationId, usedUnits).catch(() => { });
      } else {
        await metering.cancelSceneVideo(reservationId, 'no_successful_shots').catch(() => { });
      }


      if (locallyCollectedShots.length >= 2) {
        const outputName = `${currentProject._id}_scene_${scene.number}_merged`;
        let mergedUrl = null;
        try {
          const { jobId, outputUrl } = await submitTranscoderMerge(
            locallyCollectedShots.map(s => s.videoUrl || s.outputUrl), // Fix: Send strings, not objects
            outputName,
            { width: 1920, height: 1080, fps: 24 }
          );

          // Poll until done
          let state = 'PENDING';
          for (let i = 0; i < 90; i++) { // ~6 min max at 4s interval
            const j = await getTranscoderJob(jobId);
            state = j?.state || state;
            if (state === 'SUCCEEDED') { mergedUrl = outputUrl; break; }
            if (state === 'FAILED') {
              if (process.env.NODE_ENV !== 'production') console.warn('Transcoder failed', j);
              break;
            }
            await new Promise(r => setTimeout(r, 4000));
          }
        } catch (e) {
          if (process.env.NODE_ENV !== 'production') console.warn('Transcoder submit/poll error', e);
        }

        if (mergedUrl) {
          setScenes(prev => {
            const next = [...prev];
            const sc = next[index];
            if (!sc) return prev;
            next[index] = { ...sc, video: mergedUrl };
            retry(() => saveScenes(currentProject._id, next), { retries: 1, wait: 300 }).catch(() => { });
            return next;
          });
        }
      }

    } catch (err) {
      if (typeof reservationId !== 'undefined') { await metering.cancelSceneVideo(reservationId, 'outer_failure').catch(() => { }); }
      if (process.env.NODE_ENV !== 'production') console.error('Per-shot video generation failed:', err);

      alert(err?.message || 'Video generation failed');
    } finally {
      setVideoIsLoading(prev => ({ ...prev, [index]: false }));
    }
  }, [scenes, characterBible, currentProject?._id, getSelectedVideoModel]);




  // Extracted Helper: Merge shots for a scene
  const mergeSceneShots = useCallback(async (sceneIndex, overrideScenes = null) => {
    const currentScenes = overrideScenes || scenes;
    const scene = currentScenes[sceneIndex];
    if (!scene) return;

    // Collect valid video URLs from the shot matrix
    const locallyCollectedShots = [];
    const latestMatrix = scene.additionalInfo?.shotMatrix || [];
    latestMatrix.forEach((r) => {
      (Array.isArray(r.shots) ? r.shots : []).forEach((s) => {
        // Collect if videoUrl exists
        if (s.videoUrl) {
          locallyCollectedShots.push(s);
        }
      });
    });

    if (locallyCollectedShots.length >= 2) {
      // START MERGE
      setIsSceneMerging(prev => ({ ...prev, [sceneIndex]: true }));

      const outputName = `${currentProject._id}_scene_${scene.number}_merged_${Date.now()}`;
      let mergedUrl = null;
      try {
        const { jobId, outputUrl } = await submitTranscoderMerge(
          locallyCollectedShots.map(s => s.videoUrl || s.outputUrl),
          outputName,
          { width: 1920, height: 1080, fps: 24 }
        );

        // Poll until done
        let state = 'PENDING';
        for (let i = 0; i < 90; i++) { // ~6 min max at 4s interval
          const j = await getTranscoderJob(jobId);
          state = j?.state || state;
          if (state === 'SUCCEEDED') { mergedUrl = outputUrl; break; }
          if (state === 'FAILED') {
            if (process.env.NODE_ENV !== 'production') console.warn('Transcoder failed', j);
            break;
          }
          await new Promise(r => setTimeout(r, 4000));
        }
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.warn('Transcoder submit/poll error', e);
      }

      if (mergedUrl) {
        setScenes(prev => {
          const next = [...prev];
          const sc = next[sceneIndex];
          if (!sc) return prev;
          next[sceneIndex] = { ...sc, video: mergedUrl };
          // Save silently
          saveScenes(currentProject._id, next).catch(() => { });
          return next;
        });
      }

      // END MERGE
      setIsSceneMerging(prev => ({ ...prev, [sceneIndex]: false }));
    }
  }, [scenes, currentProject?._id]);

  const generateVideo2 = useCallback(async (index) => {

    if (videoIsLoading[index]) return;

    try {
      setVideoIsLoading((prev) => ({ ...prev, [index]: true }));
      const scene = scenes[index];
      const characterBibleString = generateCharacterDescriptionsConcise(scene.additionalInfo.characters, characterBible);

      const detailedPrompt = buildDetailedVideoPrompt(
        scene.additionalInfo?.visual || scene.visualPrompt,
        characterBibleString,
        scene.additionalInfo?.environment || scene.environment,
        scene.additionalInfo?.characterExperessions || scene.characterExperessions,
        {
          //beats: scene.additionalInfo?.beats,
          //shotsPerBeat: scene.additionalInfo?.shotsPerBeat,
          coveragePlan: scene.additionalInfo?.coveragePlan,
          lightingPlan: scene.additionalInfo?.lightingPlan,
          //fx: scene.additionalInfo?.fx
        }
      );

      const model = getSelectedVideoModel();
      const sceneNumber = scene?.number ?? (index + 1);
      const durationSec = 5; // matches the payload below
      const url = await metering.runSceneVideo(
        { projectId: currentProject._id, sceneNumber, durationSec, meta: { source: 'narrative-workbench', mode: 'single' } },
        async () => {
          const jobId = await createVideoJob(detailedPrompt, scene.image, { duration: durationSec }, model);
          const job = await pollJobSafe(jobId);
          return job.url;
        }
      );
      const newVideoURL = url;

      setScenes(prev => {
        const next = [...prev];
        if (!next[index]) return prev;         // guard if list changed
        next[index] = { ...next[index], video: newVideoURL };
        saveScenes(currentProject._id, next);  // persist the same snapshot
        return next;
      });

    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error regenerating video:', error);
    } finally {
      setVideoIsLoading((prev) => ({ ...prev, [index]: false }));
    }
  }, [scenes, characterBible, currentProject?._id, getSelectedVideoModel]);



  const regenerateVideo = useCallback(async (index) => {
    // Parity: Use the standard generation flow for regeneration to ensure full shot matrix processing
    // await generateVideo(index);
  }, [generateVideo]);

  const handleSmoothShot = useCallback(async (sceneIndex, shotId) => {
    if (videoIsLoading[sceneIndex]) return;

    try {
      setVideoIsLoading(prev => ({ ...prev, [sceneIndex]: true }));
      const scene = scenes[sceneIndex];
      const shotMatrix = scene?.additionalInfo?.shotMatrix || [];

      // Find the shot
      let targetShot = null;
      for (const row of shotMatrix) {
        if (Array.isArray(row.shots)) {
          targetShot = row.shots.find(s => String(s.id) === String(shotId));
          if (targetShot) break;
        }
      }

      if (!targetShot || !targetShot.videoUrl) {
        throw new Error("Target shot or video not found");
      }

      const outputName = `${currentProject._id}_scene_${scene.number}_shot_${shotId}_smooth_${Date.now()}`;

      const { jobId, outputUrl } = await submitTranscoderSmooth(
        targetShot.videoUrl,
        outputName,
        { width: 1920, height: 1080 }
      );

      // Poll
      let mergedUrl = null;
      let state = 'PENDING';
      for (let i = 0; i < 90; i++) { // ~6 min
        const j = await getTranscoderJob(jobId);
        state = j?.state || state;
        if (state === 'SUCCEEDED') { mergedUrl = outputUrl; break; }
        if (state === 'FAILED') {
          console.warn('Transcoder (Smooth) failed', j);
          break;
        }
        await new Promise(r => setTimeout(r, 4000));
      }

      if (mergedUrl) {
        // Update Shot
        setScenes(prev => {
          const next = [...prev];
          const sc = next[sceneIndex];
          if (!sc) return prev; // safety

          const rows = (sc.additionalInfo?.shotMatrix || []).map(r => ({ ...r, shots: [...(r.shots || [])] }));

          let updated = false;
          for (const row of rows) {
            const sIdx = (row.shots || []).findIndex(s => String(s.id) === String(shotId));
            if (sIdx !== -1) {
              row.shots[sIdx] = { ...row.shots[sIdx], videoUrl: mergedUrl };
              updated = true;
              break;
            }
          }

          if (updated) {
            const newScene = {
              ...sc,
              additionalInfo: { ...sc.additionalInfo, shotMatrix: rows }
            };
            next[sceneIndex] = newScene;
            saveScenes(currentProject._id, next).catch(() => { });
          }
          return next;
        });
      } else {
        throw new Error("Smoothing job failed or timed out");
      }

    } catch (error) {
      console.error("Error smoothing shot:", error);
      alert("Failed to smooth video: " + error.message);
    } finally {
      setVideoIsLoading(prev => ({ ...prev, [sceneIndex]: false }));
    }
  }, [scenes, currentProject?._id]);

  const regenerateVideoPostEdit = useCallback(async (index, newVisualPrompt) => {

    if (videoIsLoading[index]) return;

    try {
      const scene = scenes[index];
      //const newVideoURL = await generateVideoFromPrompt(newVisualPrompt);
      const model = getSelectedVideoModel();
      const sceneNumber = scenes[index]?.number ?? (index + 1);
      const durationSec = Math.ceil(Number(scenes[index]?.duration || 5));
      const url = await metering.runSceneVideo(
        { projectId: currentProject._id, sceneNumber, durationSec, meta: { source: 'narrative-workbench', mode: 'post-edit' } },
        async () => {
          const jobId = await createVideoJob(newVisualPrompt, undefined, { duration: durationSec }, model);
          const job = await pollJobSafe(jobId);
          return job.url;
        }
      );
      const newVideoURL = url;

      const updatedScenes = [...scenes];
      updatedScenes[index] = {
        ...scene,
        video: newVideoURL,
        additionalInfo: {
          ...scene.additionalInfo,
          visual: newVisualPrompt
        }
      };
      setScenes(updatedScenes);

    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error regenerating video:', error);
    }
  }, [scenes, characterBible, getSelectedVideoModel]);

  const regenerationLock = useRef(new Set());

  // NEW: Regenerate a specific shot video
  const regenerateShotVideo = useCallback(async (sceneIndex, shotId, promptOverride) => {
    if (videoIsLoading[sceneIndex]) return;

    // Prevent double-clicks
    const lockKey = `${sceneIndex}-${shotId}`;
    if (regenerationLock.current.has(lockKey)) return;
    regenerationLock.current.add(lockKey);

    try {
      setVideoIsLoading(prev => ({ ...prev, [sceneIndex]: true }));
      const scene = scenes[sceneIndex];
      const flatShots = (scene.additionalInfo?.shotMatrix || []).flatMap(r => r.shots || []);
      const shot = flatShots.find(s => String(s.id) === String(shotId));
      if (!shot) throw new Error("Shot not found");

      // Find the beat for this shot (needed for context)
      const beatRow = (scene.additionalInfo?.shotMatrix || []).find(r => (r.shots || []).some(s => String(s.id) === String(shotId))) || {};
      const beat = { beat: beatRow.beat, intent: beatRow.intent, ...beatRow };

      // Find previous shot for context parity
      let prevShot = null;
      const allShots = (scene.additionalInfo?.shotMatrix || []).flatMap(r => r.shots || []);
      const shotIndex = allShots.findIndex(s => String(s.id) === String(shotId));
      if (shotIndex > 0) {
        prevShot = allShots[shotIndex - 1];
      }

      const model = getSelectedVideoModel();
      const durationSec = 5;

      const url = await metering.runSceneVideo(
        { projectId: currentProject._id, sceneNumber: scene.number, durationSec, meta: { source: 'narrative-workbench', mode: 'shot-regenerate' } },
        async () => {
          // PARITY FIX: Use shared cinematic context
          const context = DEFAULT_CINEMATIC_CONTEXT;

          // PARITY FIX: Use normalizeCharacterBible
          const normalizedBible = normalizeCharacterBible(characterBible, scene.additionalInfo?.characters);

          const result = await generateShotVideo({
            projectId: currentProject._id,
            scene: {
              ...scene,
              // Ensure legacy standard fields are present for the builder
              scene_number: scene.number,
              scene_title: scene.title,
              scene_bible: scene.additionalInfo?.sceneBible,
            },
            beat: beat,
            shot: shot,
            prevShot: prevShot, // Pass previous shot for continuity context
            characterBible: normalizedBible,
            options: { durationSeconds: durationSec },
            model,
            promptOverride,
            context
          });

          const jobId = result.jobId;
          const job = await pollJobSafe(jobId);
          return job.url;
        }
      );

      // Update state
      setScenes(prev => {
        const next = [...prev];
        const sc = next[sceneIndex];
        if (!sc) return prev;

        const newMatrix = (sc.additionalInfo.shotMatrix || []).map(row => {
          const newShots = (row.shots || []).map(s => {
            if (String(s.id) === String(shotId)) {
              // History: keep unique video URLs, limit to last 10
              const oldHistory = Array.isArray(s.videoHistory) ? s.videoHistory : [];
              // Add existing videoUrl to history if not there
              let list = [...oldHistory];
              if (s.videoUrl && !list.includes(s.videoUrl)) list.push(s.videoUrl);
              // Add new url
              if (url && !list.includes(url)) list.push(url);

              // Filter valid strings
              list = list.filter(v => v && typeof v === 'string');

              // Cap at 10
              if (list.length > 10) {
                list = list.slice(list.length - 10);
              }

              return {
                ...s,
                videoUrl: url,
                videoHistory: list
              };
            }
            return s;
          });
          return { ...row, shots: newShots };
        });

        next[sceneIndex] = {
          ...sc,
          additionalInfo: { ...sc.additionalInfo, shotMatrix: newMatrix }
        };
        saveScenes(currentProject._id, next);

        // Auto-merge the new shot into the scene video
        mergeSceneShots(sceneIndex, next).catch(e => console.warn("Auto-merge failed", e));

        return next;
      });

    } catch (error) {
      alert("Failed to regenerate shot: " + error.message);
      console.error("Shot regen error:", error);
    } finally {
      setVideoIsLoading(prev => ({ ...prev, [sceneIndex]: false }));
      regenerationLock.current.delete(`${sceneIndex}-${shotId}`);
    }
  }, [scenes, characterBible, currentProject, getSelectedVideoModel]);

  const handleSelectShotVideo = useCallback(async (sceneIndex, shotId, url) => {
    // 1. Optimistic Update
    let updatedScenes = [];
    setScenes(prev => {
      const next = [...prev];
      const sc = next[sceneIndex];
      if (!sc) return prev;

      const newMatrix = (sc.additionalInfo.shotMatrix || []).map(row => {
        const newShots = (row.shots || []).map(s => {
          if (String(s.id) === String(shotId)) {
            return { ...s, videoUrl: url };
          }
          return s;
        });
        return { ...row, shots: newShots };
      });

      const newScene = { ...sc, additionalInfo: { ...sc.additionalInfo, shotMatrix: newMatrix } };
      next[sceneIndex] = newScene;
      updatedScenes = next; // Capture for merge
      saveScenes(currentProject._id, next);
      return next;
    });

    // 2. Trigger Merge (User confirmed selection)
    // We use the 'updatedScenes' we just calculated to ensure merge uses the new selection
    if (updatedScenes.length > 0) {
      await mergeSceneShots(sceneIndex, updatedScenes);
    }

  }, [scenes, currentProject?._id, mergeSceneShots]);


  const regenerateScreenplay = async () => {
    if (currentProject) {
      setIsScreenplayLoading(true);
      const chosen = (workflowState?.[currentProject._id]?.screenplay?.model) || 'gpt-4o';
      await handleGenerateScreenplay(currentProject, chosen);
    }
  };

  const handleGenerateVideosinBulk = useCallback(async () => {
    if (!currentProject || !Array.isArray(scenes) || scenes.length === 0) return;
    if (isBatchVideoGenRunning) return;              // prevent re-entry

    setIsBatchVideoGenRunning(true);
    const pid = currentProject._id;

    // Build a work queue of scenes that do NOT have a video yet.
    const queue = scenes
      .map((s, i) => ({ s, i }))
      .filter(({ s }) => !s?.video)
      .sort((a, b) => (a.s.number ?? a.i) - (b.s.number ?? b.i)); // stable order

    const MAX_CONCURRENCY = 5;

    const model = getSelectedVideoModel();
    const processOne = async ({ s: scene, i: index }) => {
      try {
        // per-scene spinner
        setVideoIsLoading(prev => ({ ...prev, [index]: true }));

        // Build the same prompt you already use for single-scene generation
        const characterBibleString = generateCharacterDescriptionsConcise(
          scene?.additionalInfo?.characters,
          characterBible
        );

        const detailedPrompt = buildDetailedVideoPrompt(
          scene?.additionalInfo?.visual || scene?.visualPrompt,
          characterBibleString,
          scene?.additionalInfo?.environment || scene?.environment,
          scene?.additionalInfo?.characterExperessions || scene?.characterExperessions
        );

        const poster = scene?.coverImageURL || scene?.image || undefined;

        const sceneNumber = scene?.number ?? (index + 1);
        const durationSec = 5;
        const newVideoURL = await metering.runSceneVideo(
          { projectId: pid, sceneNumber, durationSec, meta: { source: 'narrative-workbench', mode: 'bulk' } },
          async () => {
            const jobId = await createVideoJob(detailedPrompt, poster, { duration: durationSec }, model);
            const job = await pollJobSafe(jobId);
            return job.url;
          }
        );

        // Use your existing generator (wired to soraService.generateVideoFromPrompt_todo)
        // const newVideoURL = await generateVideoFromPrompt(detailedPrompt, poster);

        //const jobId = await createVideoJob(detailedPrompt, poster, { duration: 5 }, model);
        //let newVideoURL = null;
        //for (; ;) {
        //  const job = await getVideoJob(jobId);
        // you already set per-scene spinners; if desired, you could also map job.progress to a per-scene progress meter
        //  if (job?.state === 'SUCCEEDED' && job?.url) { newVideoURL = job.url; break; }
        //  if (job?.state === 'FAILED') throw new Error(job?.error || 'Video job failed');
        //  await new Promise(r => setTimeout(r, 4000));
        //}

        // Race-safe merge
        setScenes(prev => {
          const next = [...prev];
          if (!next[index]) return prev;                 // list changed mid-flight
          next[index] = { ...next[index], video: newVideoURL };
          saveScenes(pid, next);                         // persist this snapshot
          return next;
        });
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.error('Video generation failed for scene', index, e);
        // (optional) surface a toast/error per scene here
      } finally {
        setVideoIsLoading(prev => ({ ...prev, [index]: false }));
      }
    };

    // Run up to 10 workers consuming the shared queue
    const workers = Array.from({ length: Math.min(MAX_CONCURRENCY, queue.length) }, () => (async function worker() {
      while (queue.length) {
        const item = queue.shift();
        if (!item) break;
        await processOne(item);
      }
    })());

    await Promise.all(workers);

    // Mark workflow step complete only if every scene now has a video
    setScenes(prev => {
      const allHaveVideo = prev.every(sc => !!sc.video);
      if (allHaveVideo) {
        setWorkflowState(prevWF => ({
          ...prevWF,
          [pid]: { ...(prevWF[pid] || {}), videos: { complete: true, model: getSelectedVideoModel(pid) || 'veo' } }
        }));
        updateWorkflowState(pid, {
          ...workflowState[pid],
          videos: { complete: true, model: getSelectedVideoModel(pid) || 'veo' }
        });
      }
      return prev; // no scene change here—just used to gate the workflow update
    });

    setIsBatchVideoGenRunning(false);
  }, [currentProject?._id, scenes, characterBible, isBatchVideoGenRunning, getSelectedVideoModel]);


  const handleMergeScenes = async (project) => {
    setIsMerging(true);
    try {
      const sceneVideos = scenes
        .slice() // copy array
        .sort((a, b) => a.number - b.number)
        .map(scene => scene.video)
        .filter(Boolean);

      if (sceneVideos.length < 2) throw new Error("At least two scene videos are required.");

      const outputName = `${currentProject._id}_merged.mp4`;
      let mergedUrl = null;
      try {
        const { jobId, outputUrl } = await submitTranscoderMerge(
          sceneVideos,
          outputName,
          { width: 1920, height: 1080, fps: 24 }
        );

        // Poll until the merge job completes
        let state = 'PENDING';
        for (let i = 0; i < 90; i++) { // ~6 minutes at 4s interval
          const j = await getTranscoderJob(jobId);
          state = j?.state || state;
          if (state === 'SUCCEEDED') { mergedUrl = outputUrl; break; }
          if (state === 'FAILED') {
            console.warn('Transcoder failed', j);
            break;
          }
          await new Promise(r => setTimeout(r, 4000));
        }
      } catch (e) {
        console.warn('Transcoder submit/poll error', e);
      }

      if (mergedUrl) {
        // Save the clean URL to the DB
        saveMergedVideoUrl(currentProject._id, mergedUrl);

        // Append unique timestamp to force video player to reload/bypass cache
        setMergedVideoUrl(`${mergedUrl}?t=${Date.now()}`);

        // Update workflow state for "Merge" step
        const pid = currentProject._id;
        setWorkflowState(prevWF => ({
          ...prevWF,
          [pid]: { ...(prevWF[pid] || {}), merge: { complete: true, model: 'Google Veo 3.1' } }
        }));
        updateWorkflowState(pid, {
          ...workflowState[pid],
          merge: { complete: true, model: 'Google Veo 3.1' }
        });
      }

    } catch (error) {
      alert("Failed to merge scenes: " + error.message);
      console.error("Scene merge error:", error);
    } finally {
      setIsMerging(false);
    }
  };

  const toggleEditScreenplay = () => {
    setIsEditing(!isEditing);
  };

  const saveEditedScreenplay = async (editedScreenplay) => {
    if (currentProject) {
      await saveScreenplay(currentProject._id, editedScreenplay);
      setDetailedScreenplay(editedScreenplay);
      setIsEditing(false);
    }
  };



  const handleValidateScreenplayContent = async (text) => {
    try {
      const result = await validateContent(text);
      if (result && result.allowed === false) {
        return { valid: false, reason: result.reason || "Content flagged by safety policy." };
      }
      return { valid: true };
    } catch (e) {
      console.error("Content validation failed:", e);
      // Fail open or closed? Let's warn but allow save if service is down, 
      // OR fail open as per backend. Backend returns allowed=True on error.
      // So if exception propagates here, it's net-new.
      return { valid: true };
    }
  };

  const handleSelectArtifactType = (artifactType) => {
    setSelectedArtifactType(artifactType);
    // You might want to hide other sections like screenplay, scenes, etc. here
  };

  const toggleCharacterSlideshow = () => {
    setIsCharacterSlideshow(!isCharacterSlideshow);
  };

  const toggleMaximizeScreenPlay = () => {
    setMaximizeScreenPlay(!maximizeScreenPlay);
  };

  const handleSaveCharacter = async (updatedCharacter) => {
    const updated = new Map(characterBible);
    const prev = updated.get(updatedCharacter.name) || {};
    updated.set(updatedCharacter.name, { ...prev, ...updatedCharacter.details });
    setCharacterBible(updated);
    await saveCharacterBible(currentProject._id, updated);
  };

  const handleModelChange = (projectId, stepKey, model) => {
    setWorkflowState(prev => ({
      ...prev,
      [projectId]: {
        ...prev[projectId],
        [stepKey]: {
          ...prev[projectId][stepKey],
          model
        }
      }
    }));

    updateWorkflowState(projectId, {
      ...workflowState[projectId],
      [stepKey]: {
        ...workflowState[projectId]?.[stepKey],
        model
      }
    });

  };

  const openImagePlayground = () => {
    const id = (quickProjects.image && (quickProjects.image._id || quickProjects.image)) || null;
    // 1) Open immediately
    setArtifactWorkbenchMode('image');
    setSelectedArtifactType('image');
    setArtifactPrefill(null);
    setWorkbenchOptions({ quickMode: true, defaultProjectId: id });
    // 2) Load in background
    (async () => {
      let pid = id;
      if (!pid) {
        pid = await ensureProject(QUICK_IMAGE_NAME);
        setQuickProjects(prev => ({ ...prev, image: pid }));
        setWorkbenchOptions(prev => prev?.defaultProjectId ? prev : { ...prev, defaultProjectId: pid });
      }
      // Non-blocking preload for the library  sidebar
      setArtifacts(null);
      const [grouped, thumbs] = await Promise.all([
        listArtifactsSmart(pid),
        artifactStore.listRecent(pid, 'image', 1000)
      ]);
      setArtifacts(grouped);
      setQuickImages(thumbs);
    })();
  };

  const openVideoPlayground = () => {
    const id = (quickProjects.video && (quickProjects.video._id || quickProjects.video)) || null;
    setArtifactWorkbenchMode('video');
    setSelectedArtifactType('video');
    setArtifactPrefill(null);
    setWorkbenchOptions({ quickMode: true, defaultProjectId: id });
    (async () => {
      let pid = id;
      if (!pid) {
        pid = await ensureProject(QUICK_VIDEO_NAME);
        setQuickProjects(prev => ({ ...prev, video: pid }));
        setWorkbenchOptions(prev => prev?.defaultProjectId ? prev : { ...prev, defaultProjectId: pid });
      }
      setArtifacts(null);
      const [grouped, thumbs] = await Promise.all([
        listArtifactsSmart(pid),
        artifactStore.listRecent(pid, 'video', 1000)
      ]);
      setArtifacts(grouped);
      setQuickVideos(thumbs);

      (async () => {
        const enriched = await Promise.all((thumbs || []).map(async a => {
          if (a.thumbnailUrl) return a;
          const videoUrl = a.outputUrl || a.videoUrl || a.url; // whatever you store the video at
          const thumb = await getVideoFrameThumb(videoUrl, 1.0);
          return thumb ? { ...a, thumbnailUrl: thumb } : a;
        }));
        setQuickVideos(enriched);
      })();

    })();
  };

  const handleOpenArtifactFromSidebar = (artifact, kind, forceQuickMode = false) => {
    setSelectedArtifactType(kind);
    setArtifactPrefill({ artifact, kind });
    setArtifactWorkbenchMode(kind);

    const pid = (!forceQuickMode && currentProject?._id) ? currentProject._id : getQuickId(kind);

    setWorkbenchOptions({
      quickMode: forceQuickMode || !currentProject,
      defaultProjectId: pid || null,     // ensure it's an ID string/null, not an object
    });

    // Lazy-load the library used inside the workbench
    (async () => {
      if (!pid) return;
      setArtifacts(null);                // shows "Loading…"
      const grouped = await listArtifactsSmart(pid);
      setArtifacts(grouped);
    })();
  };

  const getQuickId = (kind) => {
    const v = quickProjects?.[kind];
    return v && typeof v === 'object' ? (v._id || v.id) : v || null;
  };

  const handleRegenerateFullCharacter = async (characterName, instructions) => {
    try {
      setCharacterIsRegenerating(prev => ({ ...prev, [characterName]: true }));

      const charData = characterBible.get(characterName);
      if (!charData) throw new Error("Character not found");

      // Gather context
      // Safeguard: Prepend core preservation instruction
      const safeInstructions = `IMPORTANT: You are REFINING the existing character "${characterName}". 
        PRESERVE core traits (Species, Age, Gender, Role) from currentDetails unless explicitly told to change them.
        Do not hallucinate a completely different species or being.
        
        User Instructions: ${instructions || ''}`;

      const payload = {
        characterName,
        currentDetails: charData,
        screenplayContext: currentProject?.screenplay?.scenes ? JSON.stringify(currentProject.screenplay.scenes).slice(0, 15000) : "", // Truncate to avoid token overflow/noise
        storyContext: currentProject?.story?.description || "",
        styleGuide: currentProject?.styleGuide || "",
        model: modelOptions?.characterBible || 'gpt-4o',
        instructions: safeInstructions
      };

      // 1. Regenerate Text
      const regeneratedData = await regenerateCharacterFull(payload);

      if (regeneratedData && regeneratedData[characterName]) {
        const updated = regeneratedData[characterName];

        // 2. Regenerate Image (Enrichment)
        let newImageUrl = charData.image;
        if (updated.image_generation_prompt) {
          try {
            // Force physical consistency by putting Species FIRST
            const species = updated.species || charData.species || "";
            const gender = updated.gender || charData.gender || "";
            const age = updated.age || charData.age || "";
            const anchor = updated.concise_description || updated.conciseDescription || charData.conciseDescription || "";

            const coreTraits = [species, gender, age ? `${age} years old` : ""].filter(Boolean).join(", ");

            // "A precise visual representation of a Canine (Goldendoodle), male, 4 years old. ..."
            const forcedPrompt = species
              ? `A precise visual representation of a ${species}. ${coreTraits}. ${anchor}. ${updated.image_generation_prompt}`
              : updated.image_generation_prompt;

            const b64 = await toB64IfUrl(await ImageAPI.character({
              character: forcedPrompt,
              model: modelOptions?.characterImage || 'gpt-image-1'
            }));
            newImageUrl = await saveImageOnCloudB64(b64,
              `character_${characterName}_${Date.now()}`,
              currentProject._id,
              'characters'
            );
          } catch (imgErr) {
            console.error("Image regeneration failed during full regen:", imgErr);
            // Verify we keep the old image if new one fails
          }
        }

        // Format description string using the standard utility (same as initial generation)
        const newDescription = summarizeStrictCharacter(updated);

        // 3. Save Everything (Text + Image)
        handleSaveCharacter({
          name: characterName,
          details: {
            ...charData,
            ...updated, // Overwrite details (includes snake_case strict fields)
            description: newDescription || charData.description,
            conciseDescription: updated.concise_description || updated.conciseDescription || charData.conciseDescription,
            image: newImageUrl
          }
        });
      }

    } catch (error) {
      console.error("Full character regeneration failed:", error);
      alert("Failed to regenerate character details.");
    } finally {
      setCharacterIsRegenerating(prev => ({ ...prev, [characterName]: false }));
    }
  };

  return (
    <div className="main-container">

      {toast && (
        <div className="aleph-toast">
          <svg className="aleph-toast-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <div>
            <div className="aleph-toast-title">
              {toast.status === 429 ? 'Too Many Requests' : 'Error'}
            </div>
            <div className="aleph-toast-message">{toast.message}</div>
          </div>
          <button onClick={() => setToast(null)} className="aleph-toast-close">✕</button>
        </div>
      )}

      {isWaitlisted && (
        <div className="waitlist-overlay">
          <div className="waitlist-box">
            <div className="waitlist-lock">🔒</div>
            <h2 className="waitlist-title">You’re on the private beta waitlist</h2>
            <p className="waitlist-text">
              Your account is created and your email is verified, but we’re still
              rolling out Alekhine AI to a small group of early users.
              <br />
              You’ll get an email as soon as your access is activated.
            </p>

            {/* NEW: logout button on the waitlist card */}
            <button
              type="button"
              className="waitlist-logout-btn"
              onClick={handleLogout}
            >
              ⏻
            </button>
          </div>
        </div>
      )}

      {false ? (
        <div className="header">
        </div>
      ) : null}
      <div className={`overlay ${isOverlayVisible ? 'show' : ''} `} />
      <ProjectSidebar
        projects={(projects || []).filter(
          p => p?.title !== QUICK_IMAGE_NAME && p?.title !== QUICK_VIDEO_NAME && p?.title !== QUICK_AUDIO_NAME
        )}
        onSelectProject={handleSelectProject}
        //onGenerateScreenplay={() => handleGenerateScreenplay(currentProject)}
        //onGenerateCharacterBible={() => handleGenerateCharacterBible(currentProject)}
        onGenerateScreenplay={(project, modelAlias) => handleGenerateScreenplay(project ?? currentProject, modelAlias)}
        onGenerateCharacterBible={(project, modelAlias) => handleGenerateCharacterBible(project ?? currentProject, modelAlias)}
        onGenerateScenes={handleGenerateScenes}
        onGenerateVideos={handleGenerateVideosinBulk}
        onCreateProject={() => {
          deselectProject();
          setIsOverlayVisible(true);
          setShowNewProjectDialog(true);
        }}
        onRemoveProject={handleRemoveProject}
        selectedProject={currentProject}
        logout={handleLogout}
        workflowState={workflowState}
        setWorkflowState={setWorkflowState}
        onModelChange={handleModelChange}
        isScreenplayLoading={isScreenplayLoading}
        isCharacterBibleLoading={isCharacterBibleLoading || isCharacterEnriching}
        onMergeScenes={handleMergeScenes}
        isMerging={isMerging}
        canMerge={scenes.filter(s => s.video).length >= 2}
        onSelectArtifactType={setSelectedArtifactType}   // whatever you already use
        artifacts={artifacts}
        onOpenArtifactGenerator={handleOpenArtifactFromSidebar}
        quickImages={quickImages}
        quickVideos={quickVideos}
        quickAudio={quickAudio}
        onOpenImagePlayground={openImagePlayground}
        onOpenVideoPlayground={openVideoPlayground}
        onOpenAudioPlayground={onOpenAudioPlayground}
        onOpenArtifact={(a, k) => handleOpenArtifactFromSidebar(a, k, true)}
        modelOptions={modelOptions}
        onDeleteArtifact={handleDeleteArtifact}
        artifactWorkbenchMode={artifactWorkbenchMode}
        isQuickMode={!!workbenchOptions?.quickMode}
        user={{ email: localStorage.getItem('userEmail') }}
      />

      {artifactWorkbenchMode ? (
        <ArtifactWorkbench
          mode={artifactWorkbenchMode}
          prefill={artifactPrefill}                      // NEW
          quickMode={!!workbenchOptions?.quickMode}      // NEW
          defaultProjectId={workbenchOptions?.defaultProjectId || null} // NEW
          onBack={() => {
            setArtifactWorkbenchMode(null);
            setSelectedArtifactType(null);
          }}
          projectId={currentProject?._id || workbenchOptions?.defaultProjectId || null}
          artifacts={artifacts}                // ← add this
          onSave={handleWorkbenchSave}
          modelOptions={modelOptions}
          onDelete={handleDeleteArtifact}
        />
      ) : (
        <div className="content-container">

          {showNewProjectDialog && (
            <NewProjectDialog
              onClose={() => {
                setShowNewProjectDialog(false);
                setIsOverlayVisible(false);   // ← allow background interactions again
              }}
              onCreate={handleCreateProject}
            />
          )}

          {isScreenplayLoading || isScreenplayRendered || detailedScreenplay ? (
            <div className="horizontal-screenplay-container" style={{ display: showNewProjectDialog ? 'none' : 'flex' }}>

              <NarrativeWorkbench
                /* Screenplay (unchanged) */
                detailedScreenplay={detailedScreenplay}
                screenplayOutline={screenplayOutline}
                hydratedScenesByNumber={hydratedByNumber}
                isDetailsGenerating={isDetailsGenerating}
                isScreenplayLoading={isScreenplayLoading}
                onGenerateScenes={handleGenerateScenes}
                isScenesGenerating={isScenesGenerating}
                isEditing={isEditing}
                onSaveEditedScreenplay={saveEditedScreenplay}
                onValidateContent={handleValidateScreenplayContent}
                maximizeScreenPlay={maximizeScreenPlay}
                toggleMaximizeScreenPlay={toggleMaximizeScreenPlay}
                onCancelEdit={() => setIsEditing(false)}

                /* Characters (unchanged) */
                characterBible={characterBible}
                isCharacterBibleLoading={isCharacterBibleLoading}
                isCharacterEnriching={isCharacterEnriching}
                setIsOverlayVisible={setIsOverlayVisible}
                onSaveCharacter={handleSaveCharacter}
                showCharactersPanel={(isCharacterBibleLoading || characterBible.size > 0) && maximizeScreenPlay}
                onRegenerateCharacterFull={handleRegenerateFullCharacter} // NEW
                characterIsRegenerating={characterIsRegenerating}         // NEW

                /* Scenes (unchanged) */
                scenes={scenes}
                showScenes={showScenes}
                onToggleShowScenes={() => setShowScenes(!showScenes)}
                isScenesGeneratingPanel={isScenesGenerating}
                regenerateImage={regenerateImage}
                regenerateImagePostEdit={regenerateImagePostEdit}
                imageIsRegenerating={imageIsRegenerating} // NEW prop
                onSelectSceneImage={handleSelectSceneImage}
                generateVideo={generateVideo}
                regenerateVideo={regenerateVideo}
                regenerateVideoPostEdit={regenerateVideoPostEdit}
                onRegenerateShot={regenerateShotVideo} // NEW
                onSelectShotVideo={handleSelectShotVideo}
                showScenesPanel={!selectedArtifactType || selectedArtifactType === 'scene'}
                videoIsLoading={videoIsLoading}
                isMerging={isSceneMerging} // NEW: Pass merging state
                videoDots={videoDots}

                /* Final Movie (NEW) */
                mergedVideoUrl={mergedVideoUrl}
                showFinalMoviePanel={!!mergedVideoUrl}
                finalMovieTitle={currentProject?.title || ''}
                modelOptions={modelOptions}
                onMergeScenes={() => handleMergeScenes(currentProject)} // NEW: Global remerge
                isGlobalMerging={isMerging} // NEW: Global merge state

                /* Optional loading asset (used by scenes header) */
                loadingGifUrl={`${process.env.PUBLIC_URL}/static/media/loading1.eb6327c2b020fd151d29.gif`}

                /* NEW: screenplay header tool actions */
                onRegenerateScreenplay={regenerateScreenplay}
                onEditScreenplay={toggleEditScreenplay}
                onDownloadScreenplay={() => {
                  // lightweight download helper
                  if (!detailedScreenplay) return;
                  const blob = new Blob([detailedScreenplay], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${(currentProject?.title || 'screenplay').replace(/\s+/g, '_')}.txt`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                }}
                onEnhanceScreenplay={toggleCharacterSlideshow}
                projectId={currentProject?._id}

              />
            </div>
          ) : null}

        </div>
      )}
      {isActive && <TourTooltip />}
    </div>
  );
};

export default MainPage;
